import { brand } from "../../../config/theme";
import { clusterSegments, clusterUnitRows, segmentsKey } from "./clusterRing";
import ClusterTile from "./ClusterTile";
import { trackDiagnostic } from "../../../utils/diagnostics";
import { STOP_MARKER_ICON_URL, STOP_MARKER_SIZE, buildStopPopupHtml } from "../../../utils/mapStopMarker";
import { useCallback, useEffect, useState, useRef, useMemo, memo, Fragment } from "react";
import { createPortal } from "react-dom";
import {
  useJsApiLoader,
  GoogleMap,
  OverlayView,
  Polygon,
  TrafficLayer,
  TransitLayer,
  BicyclingLayer,
  Marker,
} from "@react-google-maps/api";
import {
  buildCorridorRing,
  resolveLinearAxis,
  LINEAR_BUFFER_DEFAULT,
} from "../../../utils/geofenceCorridor";
import { useTranslation } from "react-i18next";
import { mapBaseStyles } from "../../../components/map/mapBaseStyles";
import { useFilteredSocketVehicles } from "../../../store/admin/vehiclesSocketSelector";
import { useVehiclesSocketStore } from "../../../store/admin/vehiclesSocketStore";
import { useMapReady } from "../../../hooks/useMapReady";
import {
  animarHacia,
  debeAnimar,
  esTramoRecto,
  curvaEntre,
  metrosEntre,
  duracionPara,
  TRAMO_MAX_M,
  type Animacion,
  type Punto,
} from "../../../components/map/movimientoSuave";
import { DRAWN_POSITIONS } from "../../../components/map/drawnPositions";
import MapLoadingOverlay from "./MapLoadingOverlay";
import {
  DEVICES_LOAD_ERRORS,
  type DevicesLoadErrorCode,
} from "../../../features/devices/devicesDiagnostics";
import { useDevicesFilterDiagnostics } from "../../../features/devices/useDevicesFilterDiagnostics";
import { connectSocket } from "../../../socket";
import { useLiveDataPanelStore } from "../../../store/lite/liveDataPanelStore";
import { useGeofencesList } from "../../../features/geofences/queries";
import { usePointsInterestList } from "../../../features/pointsInterest/queries";
import { useStatusesStore } from "../../../store/admin/statusesStore";
import {
  useVehicleRouteTrackingStore,
  buildRouteSegments,
  type VehicleTracking,
  type RouteSegment,
  type RouteStateMarker,
} from "../../../store/admin/vehicleRouteTrackingStore";
import { useVehicleFootprintTrackingStore, type VehicleFootprint } from "../../../store/admin/vehicleFootprintTrackingStore";
import { type PointOfInterest } from "../../../store/admin/pointOfInterest";
import { useRoutesList } from "../../../features/routes/queries";
import { useUserStore } from "../../../store/userStore";
import staticVehicles from "../json_vehicles/vehicles.json";
import { useOutletContext } from "react-router-dom";

import { useDarkMode } from "../../../context/ThemeContext";
import { mapChrome, mapChromeBase, mapIconButton } from "../../../components/ui/mapChrome";
import UnitInfoModal, { MINIMIZE_EVENT } from "./modals/UnitInfoModal";
import { hasMapPosition } from "../../../utils/hasMapPosition";
import AddressSearch from "./AddressSearch";
import VehiclePointer from "./VehiclePointer";
import ClassicV4Marker from "./ClassicV4Marker";
import UnitLabel from "../../../features/unitDisplay/UnitLabel";
import UnitDisplayTool from "../../../features/unitDisplay/UnitDisplayTool";
import { useUnitDisplay } from "../../../features/unitDisplay/unitDisplayStore";
import { visibleLabelIds } from "../../../features/unitDisplay/labelCollision";
import { resolveVehicleModel } from "../../../config/vehicleModels";
import { liveSpeedKmh } from "../../../Layouts/lite/components/Sidebar/engineDisplay";
import { rowSpeed } from "../../../Layouts/lite/components/Sidebar/compactRow";
import { useDriveView } from "../../../features/driveView/driveViewStore";
import { canDrawDriveView } from "../../../features/driveView/webgl";

// Importar H3
import { latLngToCell, cellToBoundary } from "h3-js";

type MapTypeId = "roadmap" | "satellite" | "hybrid" | "terrain";

// Recorrido histórico: una polyline por tramo, coloreada según el estado
// (driving / ralenti / stopped). El recorrido es estático tras cargarse, así
// que recreamos las polylines cuando cambian los segmentos.
const RouteHistoryPolylines = ({
  segments,
  map,
}: {
  segments: RouteSegment[];
  map: google.maps.Map;
}) => {
  const linesRef = useRef<google.maps.Polyline[]>([]);

  useEffect(() => {
    if (!map) return;

    const lines = segments
      .filter((s) => s.path.length >= 2)
      .map(
        (s) =>
          new google.maps.Polyline({
            path: s.path,
            strokeColor: s.color,
            strokeOpacity: 0.9,
            strokeWeight: 4,
            clickable: false,
            zIndex: 3,
            icons: [
              {
                icon: {
                  path: google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
                  scale: 3,
                  strokeColor: "#ffffff",
                  strokeWeight: 1.5,
                  fillColor: s.color,
                  fillOpacity: 1,
                },
                offset: "50%",
              },
            ],
          }),
      );

    lines.forEach((l) => l.setMap(map));
    linesRef.current = lines;

    return () => {
      linesRef.current.forEach((l) => l.setMap(null));
      linesRef.current = [];
    };
  }, [map, segments]);

  return null;
};

// Componente de Bandera de Inicio para trackings (Route y Footprint)
const FLAG_SIZE = 28;
const FLAG_OFFSET = {
  x: -FLAG_SIZE / 2,
  y: -FLAG_SIZE,
};

interface StartFlagMarkerProps {
  lat: number;
  lng: number;
  color: string;
  label?: string;
  map: google.maps.Map;
  type: "route" | "footprint";
}

const StartFlagMarker = memo(
  ({ lat, lng, color, map, type: _type }: StartFlagMarkerProps) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);

    useEffect(() => {
      if (!map) return;

      const div = document.createElement("div");
      div.style.position = "absolute";
      div.style.pointerEvents = "none";

      class FlagOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) {
            panes.overlayMouseTarget.appendChild(this.div);
            // Que el clic sobre el overlay no llegue también al mapa: sin esto
            // tocar una unidad era además "clic en el mapa", que suelta el
            // seguimiento; seleccionar no funcionaba (video 6-oct-2026).
            google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.div);
          }
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;

          const pos = projection.fromLatLngToDivPixel(this.position);
          if (pos) {
            this.div.style.left = `${pos.x + FLAG_OFFSET.x}px`;
            this.div.style.top = `${pos.y + FLAG_OFFSET.y}px`;
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }

        updatePosition(newPosition: google.maps.LatLng) {
          this.position = newPosition;
          this.draw();
        }
      }

      const position = new google.maps.LatLng(lat, lng);
      const overlay = new FlagOverlay(position, div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
        setContainer(null);
      };
    }, [map, lat, lng]);

    useEffect(() => {
      if (overlayRef.current) {
        const newPosition = new google.maps.LatLng(lat, lng);
        (overlayRef.current as any).updatePosition(newPosition);
      }
    }, [lat, lng]);

    if (!container) return null;

    return createPortal(
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Icono de bandera simple */}
        <div
          style={{
            width: `${FLAG_SIZE}px`,
            height: `${FLAG_SIZE}px`,
            backgroundColor: color,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
            border: "2px solid #fff",
          }}
        >
          <i
            className="ri-flag-fill"
            style={{
              color: "#fff",
              fontSize: "14px",
            }}
          />
        </div>
      </div>,
      container,
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.lat === nextProps.lat &&
      prevProps.lng === nextProps.lng &&
      prevProps.color === nextProps.color &&
      prevProps.type === nextProps.type &&
      prevProps.map === nextProps.map
    );
  },
);

// Componente de Bandera Final (sigue al vehiculo)
const END_FLAG_SIZE = 22;
const END_FLAG_OFFSET = {
  x: -END_FLAG_SIZE / 2,
  y: -END_FLAG_SIZE,
};

interface EndFlagMarkerProps {
  lat: number;
  lng: number;
  color: string;
  map: google.maps.Map;
  type: "route" | "footprint";
}

const EndFlagMarker = memo(
  ({ lat, lng, color, map, type: _type }: EndFlagMarkerProps) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);

    useEffect(() => {
      if (!map) return;

      const div = document.createElement("div");
      div.style.position = "absolute";
      div.style.pointerEvents = "none";
      div.style.zIndex = "100";

      class EndFlagOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) {
            panes.overlayMouseTarget.appendChild(this.div);
            // Que el clic sobre el overlay no llegue también al mapa: sin esto
            // tocar una unidad era además "clic en el mapa", que suelta el
            // seguimiento; seleccionar no funcionaba (video 6-oct-2026).
            google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.div);
          }
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;

          const pos = projection.fromLatLngToDivPixel(this.position);
          if (pos) {
            this.div.style.left = `${pos.x + END_FLAG_OFFSET.x}px`;
            this.div.style.top = `${pos.y + END_FLAG_OFFSET.y}px`;
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }

        updatePosition(newPosition: google.maps.LatLng) {
          this.position = newPosition;
          this.draw();
        }
      }

      const position = new google.maps.LatLng(lat, lng);
      const overlay = new EndFlagOverlay(position, div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
        setContainer(null);
      };
    }, [map]);

    // Actualizar posicion cuando cambian las coordenadas (vehiculo en movimiento)
    useEffect(() => {
      if (overlayRef.current) {
        const newPosition = new google.maps.LatLng(lat, lng);
        (overlayRef.current as any).updatePosition(newPosition);
      }
    }, [lat, lng]);

    if (!container) return null;

    return createPortal(
      <div
        style={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Icono de bandera simple */}
        <div
          style={{
            width: `${END_FLAG_SIZE}px`,
            height: `${END_FLAG_SIZE}px`,
            backgroundColor: color,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            boxShadow: "0 2px 6px rgba(0, 0, 0, 0.3)",
            border: "2px solid #fff",
          }}
        >
          <svg
            width="13"
            height="13"
            viewBox="0 0 16 16"
            fill="none"
          >
            <path
              d="M3.4 1.5V14.5"
              stroke="#fff"
              strokeWidth="1.6"
              strokeLinecap="round"
            />
            <rect x="5" y="2.2" width="2.7" height="2.7" fill="#fff" />
            <rect x="10.4" y="2.2" width="2.7" height="2.7" fill="#fff" />
            <rect x="7.7" y="4.9" width="2.7" height="2.7" fill="#fff" />
            <rect x="5" y="7.6" width="2.7" height="2.7" fill="#fff" />
            <rect x="10.4" y="7.6" width="2.7" height="2.7" fill="#fff" />
          </svg>
        </div>
      </div>,
      container,
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.lat === nextProps.lat &&
      prevProps.lng === nextProps.lng &&
      prevProps.color === nextProps.color &&
      prevProps.type === nextProps.type &&
      prevProps.map === nextProps.map
    );
  },
);

// Componente para marcadores de paradas
interface StopMarkersProps {
  stateMarkers: RouteStateMarker[];
  map: google.maps.Map;
}

const StopMarkers = ({ stateMarkers, map }: StopMarkersProps) => {
  const markersRef = useRef<google.maps.Marker[]>([]);
  const infoWindowRef = useRef<google.maps.InfoWindow | null>(null);
  const geocoderRef = useRef<google.maps.Geocoder | null>(null);

  useEffect(() => {
    if (!map) return;

    // Inicializar Geocoder
    if (!geocoderRef.current) {
      geocoderRef.current = new google.maps.Geocoder();
    }

    // Limpiar marcadores anteriores
    markersRef.current.forEach(marker => marker.setMap(null));
    markersRef.current = [];

    // Cerrar InfoWindow si existe
    if (infoWindowRef.current) {
      infoWindowRef.current.close();
    }

    // Crear marcadores solo para paradas (stopped)
    const stopMarkers = stateMarkers.filter(m => m.status === "stopped");

    // SVG ícono moderno de advertencia de parada


    const extractLocationInfo = (results: google.maps.GeocoderResult[]) => {
      if (!results || results.length === 0) return { state: "—", city: "—", street: "—" };

      const result = results[0];
      let state = "—", city = "—", street = "—";

      result.address_components.forEach((component) => {
        const types = component.types;
        if (types.includes("administrative_area_level_1")) {
          state = component.long_name;
        } else if (types.includes("locality")) {
          city = component.long_name;
        } else if (types.includes("route")) {
          street = component.long_name;
        }
      });

      return { state, city, street };
    };

    stopMarkers.forEach(marker => {
      const googleMarker = new google.maps.Marker({
        position: { lat: marker.lat, lng: marker.lng },
        map,
        title: `Parada: ${Math.round(marker.durationMs / 60000)} minutos`,
        icon: {
          url: STOP_MARKER_ICON_URL,
          scaledSize: new google.maps.Size(STOP_MARKER_SIZE, STOP_MARKER_SIZE),
          anchor: new google.maps.Point(STOP_MARKER_SIZE / 2, STOP_MARKER_SIZE / 2),
        },
      });

      googleMarker.addListener('click', () => {
        const popupData = {
          fecha: new Date(marker.ts).toLocaleString("es-ES", {
            day: "2-digit",
            month: "2-digit",
            year: "numeric",
            hour: "2-digit",
            minute: "2-digit",
            second: "2-digit",
            hourCycle: "h23",
          }),
          durationMs: marker.durationMs,
          lat: marker.lat,
          lng: marker.lng,
        };

        infoWindowRef.current?.close();

        infoWindowRef.current = new google.maps.InfoWindow({
          content: buildStopPopupHtml({ ...popupData, location: null }),
          maxWidth: 300,
        });

        infoWindowRef.current.open(map, googleMarker);

        if (!geocoderRef.current) return;

        geocoderRef.current.geocode(
          { location: { lat: marker.lat, lng: marker.lng } },
          (results, status) => {
            if (status !== google.maps.GeocoderStatus.OK || !results || !infoWindowRef.current) return;
            const { state, city, street } = extractLocationInfo(results);
            infoWindowRef.current.setContent(
              buildStopPopupHtml({ ...popupData, location: `${state}, ${city}, ${street}` }),
            );
          },
        );
      });

      markersRef.current.push(googleMarker);
    });

    return () => {
      markersRef.current.forEach(marker => marker.setMap(null));
      markersRef.current = [];
      if (infoWindowRef.current) {
        infoWindowRef.current.close();
      }
    };
  }, [map, stateMarkers]);

  return null;
};

interface GeofenceAxisPolylineProps {
  color: string;
  path: google.maps.LatLngLiteral[];
  map: google.maps.Map | null;
}

const GeofenceAxisPolyline = ({ color, path, map }: GeofenceAxisPolylineProps) => {
  const polylineRef = useRef<google.maps.Polyline | null>(null);
  const pathKey = JSON.stringify(path);

  useEffect(() => {
    if (!map || path.length < 2) return;

    const polyline = new google.maps.Polyline({
      path,
      strokeColor: color,
      strokeOpacity: 0.9,
      strokeWeight: 3,
      clickable: false,
      zIndex: 3,
    });

    polyline.setMap(map);
    polylineRef.current = polyline;

    return () => {
      polyline.setMap(null);
      polylineRef.current = null;
    };
  }, [map, color, pathKey]);

  return null;
};

// Componente Polyline para Footprint (igual que VehicleRoutePolyline pero para footprints)
interface FootprintPolylineProps {
  vehicleId: string;
  color: string;
  points: Array<{ lat: number; lng: number }>;
  map: google.maps.Map;
}

const VehicleFootprintPolyline = ({
  vehicleId,
  color,
  points,
  map,
}: FootprintPolylineProps) => {
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  useEffect(() => {
    if (!map) {
      return;
    }

    const polyline = new google.maps.Polyline({
      path: points.length >= 2 ? points : [],
      strokeColor: color,
      strokeOpacity: 0.9,
      strokeWeight: 4,
      clickable: false,
      zIndex: 3,
    });

    polyline.setMap(map);
    polylineRef.current = polyline;

    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [map, vehicleId, color]);

  useEffect(() => {
    if (polylineRef.current && points.length >= 2) {
      polylineRef.current.setPath(points);
    }
  }, [points]);

  return null;
};

// Polyline that follows the actual driving path between route points,
// computed via Google's DirectionsService (same as the route creation form).
// Falls back to a straight polyline if directions fail.
interface RouteDirectionsPolylineProps {
  routeId: string;
  points: Array<{ lat: number; lng: number }>;
  axis: Array<{ lat: number; lng: number }> | null;
  buffer: number;
  firstPointRadius: number;
  lastPointRadius: number;
  color: string;
  map: google.maps.Map;
}

const directionsPathCache = new Map<string, google.maps.LatLng[]>();

const routePathKey = (
  routeId: string,
  points: Array<{ lat: number; lng: number }>,
) => {
  const first = points[0];
  const last = points[points.length - 1];
  return `${routeId}:${points.length}:${first.lat},${first.lng}:${last.lat},${last.lng}`;
};

const RouteDirectionsPolyline = memo(
  ({
    routeId,
    points,
    axis,
    buffer,
    firstPointRadius,
    lastPointRadius,
    color,
    map,
  }: RouteDirectionsPolylineProps) => {
    const polylineRef = useRef<google.maps.Polyline | null>(null);
    const markersRef = useRef<google.maps.Marker[]>([]);
    const corridorRef = useRef<google.maps.Polygon | null>(null);
    const zoneCirclesRef = useRef<google.maps.Circle[]>([]);

    useEffect(() => {
      if (!map || points.length < 2) return;

      let cancelled = false;

      const clearOverlays = () => {
        if (polylineRef.current) {
          polylineRef.current.setMap(null);
          polylineRef.current = null;
        }
        for (const m of markersRef.current) m.setMap(null);
        markersRef.current = [];
        if (corridorRef.current) {
          corridorRef.current.setMap(null);
          corridorRef.current = null;
        }
        for (const c of zoneCirclesRef.current) c.setMap(null);
        zoneCirclesRef.current = [];
      };

      const drawCorridorAndZones = (path: Array<{ lat: number; lng: number }>) => {
        if (cancelled || path.length < 2) return;

        if (buffer > 0) {
          const ring = buildCorridorRing(path, buffer);
          if (ring.length > 0) {
            corridorRef.current = new google.maps.Polygon({
              paths: ring,
              strokeColor: color,
              strokeOpacity: 0.7,
              strokeWeight: 1,
              fillColor: color,
              fillOpacity: 0.18,
              clickable: false,
              zIndex: 1,
              map,
            });
          }
        }

        const zones = [
          { center: path[0], radius: firstPointRadius, color: "#10b981" },
          { center: path[path.length - 1], radius: lastPointRadius, color: "#ef4444" },
        ];
        zones.forEach(({ center, radius, color: zoneColor }) => {
          if (!(radius > 0)) return;
          zoneCirclesRef.current.push(
            new google.maps.Circle({
              center,
              radius,
              strokeColor: zoneColor,
              strokeOpacity: 0.9,
              strokeWeight: 2,
              fillColor: zoneColor,
              fillOpacity: 0.1,
              clickable: false,
              zIndex: 1,
              map,
            })
          );
        });
      };

      const drawStraight = () => {
        if (cancelled) return;
        polylineRef.current = new google.maps.Polyline({
          path: points,
          strokeColor: color,
          strokeOpacity: 0.9,
          strokeWeight: 4,
          clickable: false,
          zIndex: 2,
          map,
        });
      };

      // Origin / via / destination markers — same color scheme as the
      // route creation preview (emerald / amber / red).
      const drawPointMarkers = () => {
        if (cancelled) return;
        points.forEach((p, idx) => {
          const isFirst = idx === 0;
          const isLast = idx === points.length - 1;
          const fill = isFirst ? "#10b981" : isLast ? "#ef4444" : "#f59e0b";
          const marker = new google.maps.Marker({
            position: p,
            map,
            clickable: false,
            zIndex: 3,
            icon: {
              path: google.maps.SymbolPath.CIRCLE,
              scale: isFirst || isLast ? 6 : 4,
              fillColor: fill,
              fillOpacity: 1,
              strokeColor: "#ffffff",
              strokeWeight: 2,
            },
          });
          markersRef.current.push(marker);
        });
      };

      // Need at least origin + destination; up to 25 waypoints (Google limit)
      const origin = points[0];
      const destination = points[points.length - 1];
      const waypoints = points.slice(1, -1).slice(0, 25).map((p) => ({
        location: p,
        stopover: false,
      }));

      const drawResolved = (
        path: google.maps.LatLng[] | google.maps.LatLngLiteral[]
      ) => {
        if (cancelled) return;
        polylineRef.current = new google.maps.Polyline({
          path,
          strokeColor: color,
          strokeOpacity: 0.9,
          strokeWeight: 4,
          clickable: false,
          zIndex: 2,
          map,
        });
      };

      // El eje guardado por el backend es el que evalua la ingesta: si existe
      // se dibuja tal cual, sin volver a pedir el trazado a Directions.
      if (axis && axis.length >= 2) {
        drawResolved(axis);
        drawCorridorAndZones(axis);
        drawPointMarkers();
        return () => {
          cancelled = true;
          clearOverlays();
        };
      }

      const cacheKey = routePathKey(routeId, points);
      const cachedPath = directionsPathCache.get(cacheKey);

      if (cachedPath) {
        drawResolved(cachedPath);
        drawCorridorAndZones(cachedPath.map((p) => ({ lat: p.lat(), lng: p.lng() })));
        drawPointMarkers();
      } else {
        const service = new google.maps.DirectionsService();
        service.route(
          {
            origin,
            destination,
            waypoints,
            travelMode: google.maps.TravelMode.DRIVING,
            optimizeWaypoints: false,
          },
          (result, status) => {
            if (cancelled) return;
            if (
              status === google.maps.DirectionsStatus.OK &&
              result?.routes?.[0]?.overview_path
            ) {
              directionsPathCache.set(
                cacheKey,
                result.routes[0].overview_path,
              );
              drawResolved(result.routes[0].overview_path);
              drawCorridorAndZones(
                result.routes[0].overview_path.map((p) => ({
                  lat: p.lat(),
                  lng: p.lng(),
                }))
              );
            } else {
              // Directions failed (e.g. quota / no route): keep the straight line
              drawStraight();
              drawCorridorAndZones(points);
            }
            drawPointMarkers();
          },
        );
      }

      return () => {
        cancelled = true;
        clearOverlays();
      };
    }, [
      map,
      routeId,
      color,
      points,
      axis,
      buffer,
      firstPointRadius,
      lastPointRadius,
    ]);

    return null;
  },
  (prev, next) =>
    prev.map === next.map &&
    prev.routeId === next.routeId &&
    prev.color === next.color &&
    prev.points === next.points,
);

interface OutletContextType {
  showGrid: boolean;
  mapType: MapTypeId | null;
  setMapType: (type: MapTypeId) => void;
  showTraffic: boolean;
  showTransit: boolean;
  showBicycling: boolean;
  showGeofences: boolean;
  setShowGeofences: (show: boolean) => void;
  geofenceFilter: "all" | "linear" | "polygonal" | "circular";
  setGeofenceFilter: (
    filter: "all" | "linear" | "polygonal" | "circular",
  ) => void;
  showInterestPoints: boolean;
  setShowInterestPoints: (show: boolean) => void;
  showRoutes: boolean;
  setShowRoutes: (show: boolean) => void;
  showRuler: boolean;
  setShowRuler: (show: boolean) => void;
  showIcons: boolean;
  setShowIcons: (show: boolean) => void;
  showVehicleLabels?: boolean;
  showSearchBox: boolean;
  setShowSearchBox: (show: boolean) => void;
}

const DEFAULT_CENTER = { lat: 23.6345, lng: -102.5528 };
const DEFAULT_ZOOM = 5;
// Zoom al empezar a seguir una unidad. Con los punteros a tamaño real, en 18
// la unidad se veía diminuta (tracto ~13 px); en 19 se ve bien (~26 px) sin
// acercar tanto que cada reporte mueva medio mapa. Tope del mapa: 20.
const FOCUS_ZOOM = 19;
const ENABLE_STATIC_VEHICLES = false; // Set to false or comment out the import above to remove fake units
const H3_ZOOM_MAPPING: { [key: number]: number } = {
  4: 1, // Zoom 4 -> Res 1
  5: 2, // Zoom 5 -> Res 2
  6: 2, // Zoom 6 -> Res 2
  7: 3, // Zoom 7 -> Res 3
  8: 3, // Zoom 8 -> Res 3
  9: 4, // Zoom 9 -> Res 4
  10: 5, // Zoom 10 -> Res 5
  11: 6, // Zoom 11 -> Res 6
  12: 6, // Zoom 12 -> Res 6
  13: 7, // Zoom 13 -> Res 7
  14: 8, // Zoom 14 -> Res 8
  15: 8, // Zoom 15 -> Res 8
  16: 9, // Zoom 16 -> Res 9
  17: 10, // Zoom 17 -> Res 10
  18: 10, // Zoom 18 -> Res 10
  19: 11, // Zoom 19 -> Res 11
  20: 12, // Zoom 20 -> Res 12
  21: 13, // Zoom 20 -> Res 13
  22: 13, // Zoom 20 -> Res 13
};


const GOOGLE_MAPS_LIBRARIES: ("places" | "drawing" | "geometry")[] = ["places"];

// Color de la unidad enfocada/seleccionada (distinto de los colores de estado)
// Unidad seleccionada: aro gris oscuro + sombra difuminada (antes morado
// #A855F7, que no era de la marca y se leía como un estado más).
const FOCUS_COLOR = "#374151";
const FOCUS_GLOW = "rgba(0, 0, 0, 0.35)";
const FOCUS_PANEL_OFFSET_X = -180;
const COMPACT_VIEWPORT_MAX_WIDTH = 768;

// Colores para los estados de vehículos
const VEHICLE_STATUS_COLORS: { [key: string]: string } = {
  driving: brand.primary,
  sleep: "#3B82F6",
  stopped: "#EF4444",
  offline: "#6B7280",
  ralenti: "#F59E0B",
};

// Tamaño específico para el marcador de "driving" (más pequeño que los demás)
const DRIVING_MARKER_SIZE = 30;
const DRIVING_BORDER_WIDTH = 3;

// Función para determinar la forma del marcador según el estado
const getMarkerShape = (status: string, vehicle: any) => {
  switch (status) {
    case "driving": {
      // La flecha de "driving" solo tiene sentido si tenemos heading real para
      // rotarla. Cuando el provider manda únicamente lat/lon (caso RDB), course
      // queda en 0/null y la flecha apuntaría siempre al norte engañando al
      // usuario. En ese caso usamos el shape default (círculo con truck icon),
      // pero el color sigue siendo brand.primary porque viene de
      // VEHICLE_STATUS_COLORS["driving"].
      const hasHeading = vehicle.course != null && vehicle.course !== 0;
      if (!hasHeading) {
        return {
          shape: "circle",
          icon: "ri-truck-line",
          borderRadius: "50%",
          clipPath: undefined,
          rotation: undefined,
          size: MARKER_SIZE,
          hasBorder: false,
          borderWidth: 0,
        };
      }
      return {
        shape: "polygon",
        icon: null,
        clipPath: "polygon(50% 0%, 100% 100%, 50% 75%, 0% 100%)",
        rotation: vehicle.course,
        borderRadius: undefined,
        size: DRIVING_MARKER_SIZE,
        hasBorder: true,
        borderWidth: DRIVING_BORDER_WIDTH,
      };
    }
    case "stopped":
      return {
        shape: "square",
        icon: "ri-stop-line",
        borderRadius: "0%",
        clipPath: undefined,
        rotation: undefined,
        size: MARKER_SIZE,
        hasBorder: false,
        borderWidth: 0,
      };
    case "idle":
    case "ralenti":
      return {
        shape: "circle",
        icon: "ri-pause-circle-line",
        borderRadius: "50%",
        clipPath: undefined,
        rotation: undefined,
        size: MARKER_SIZE,
        hasBorder: false,
        borderWidth: 0,
      };
    default:
      return {
        shape: "circle",
        icon: "ri-truck-line",
        borderRadius: "50%",
        clipPath: undefined,
        rotation: undefined,
        size: MARKER_SIZE,
        hasBorder: false,
        borderWidth: 0,
      };
  }
};

// Tamaño fijo del marcador para cálculo preciso del offset
const MARKER_SIZE = 32;

// Offset constante para CustomMarker - evita recreación en cada render
const CUSTOM_MARKER_OFFSET = {
  x: 0,
  y: 0,
};

// Componente CustomMarker usando google.maps.OverlayView nativo para evitar parpadeo
// DRAWN_POSITIONS (components/map/drawnPositions): dónde se DIBUJA cada unidad.
// El seguimiento centra ahí y no en el último reporte: centrar en el dato real
// dejaba la unidad fuera del centro hasta que el dibujo la alcanzaba.

const CustomMarker = memo(
  ({
    vehicle,
    onClick,
    isFocused,
    map,
    showLabel = true,
    onDrawn,
    zoom = DEFAULT_ZOOM,
  }: {
    vehicle: any;
    onClick: () => void;
    isFocused: boolean;
    map: google.maps.Map | null;
    showLabel?: boolean;
    /** Sólo la unidad seguida: la cámara se centra en cada cuadro dibujado. */
    onDrawn?: (p: Punto) => void;
    /** Zoom al que se dibuja el puntero (se toma al EMPEZAR el zoom). */
    zoom?: number;
  }) => {
    const model = resolveVehicleModel(vehicle.vehicle?.pointerModel, vehicle.vehicle?.bodyType, vehicle.vehicle?.type);
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const onDrawnRef = useRef(onDrawn);
    onDrawnRef.current = onDrawn;
    const overlayRef = useRef<google.maps.OverlayView | null>(null);
    // Misma regla que la fila de la lista: velocidad sólo si se mueve (≥ 1
    // km/h). "0 km/h" en cada unidad detenida es ruido que tapa el mapa.
    const labelSpeed = rowSpeed(liveSpeedKmh(vehicle.speed, vehicle.status));

    const markerColor = VEHICLE_STATUS_COLORS[vehicle.status] || "#6B7280";
    const markerStyle = getMarkerShape(vehicle.status, vehicle);
    const unitDisplay = useUnitDisplay();
    const markerSize = Math.round(markerStyle.size * unitDisplay.icon_scale);

    useEffect(() => {
      if (!map) return;

      const div = document.createElement("div");
      div.style.position = "absolute";
      // Con nombres a la vista, dos unidades juntas enciman sus etiquetas: la
      // que está bajo el cursor pasa al frente (index.css) para poder leerla.
      div.className = "dumax-vehicle-marker";

      class CustomOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) {
            panes.overlayMouseTarget.appendChild(this.div);
            // Que el clic sobre el overlay no llegue también al mapa: sin esto
            // tocar una unidad era además "clic en el mapa", que suelta el
            // seguimiento; seleccionar no funcionaba (video 6-oct-2026).
            google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.div);
          }
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;

          const pos = projection.fromLatLngToDivPixel(this.position);
          if (pos) {
            this.div.style.transform = "translate(-50%, -50%)"; this.div.style.left = `${pos.x + CUSTOM_MARKER_OFFSET.x}px`;
            this.div.style.top = `${pos.y + CUSTOM_MARKER_OFFSET.y}px`;
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }

        updatePosition(newPosition: google.maps.LatLng) {
          this.position = newPosition;
          this.draw();
        }

        getDiv() {
          return this.div;
        }
      }

      const position = new google.maps.LatLng(
        Number(vehicle.latitude),
        Number(vehicle.longitude),
      );

      const overlay = new CustomOverlay(position, div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
        // Sin marcador (agrupada bajo zoom 10, íconos ocultos, mapa desmontado) la
        // posición dibujada se congelaba y la vista de manejo la seguía usando: la
        // unidad no avanzaba (Daniel en el teléfono, 29-oct). Sin entrada, la vista
        // cae al último reporte.
        DRAWN_POSITIONS.delete(vehicle.id);
        setContainer(null);
      };
    }, [map, vehicle.id]);

    const ultimaPosRef = useRef<Punto | null>(null);
    const ultimaAlMs = useRef<number>(0);
    const dibujadaRef = useRef<Punto | null>(null);
    const animacionRef = useRef<Animacion | null>(null);
    const ultimaGpsS = useRef(0);
    const ultimoRumboRef = useRef<number | null>(null);

    useEffect(() => {
      const overlay = overlayRef.current as any;
      if (!overlay || !vehicle.latitude || !vehicle.longitude) return;

      const destino = {
        lat: Number(vehicle.latitude),
        lng: Number(vehicle.longitude),
      };
      if (!Number.isFinite(destino.lat) || !Number.isFinite(destino.lng)) return;

      // Mismo punto otra vez (reportes de estado/evento sin moverse): no
      // cortar el tramo en curso, que lo hacía brincar a su destino.
      if (ultimaPosRef.current && metrosEntre(ultimaPosRef.current, destino) < 1) return;

      animacionRef.current?.cancelar();
      animacionRef.current = null;

      const aplicar = (p: Punto) => {
        dibujadaRef.current = p;
        DRAWN_POSITIONS.set(vehicle.id, p);
        overlay.updatePosition(new google.maps.LatLng(p.lat, p.lng));
        // La cámara de la unidad seguida se mueve en el MISMO cuadro que el
        // marcador. Con dos animaciones (cámara y marcador) con relojes
        // distintos el marcador vibraba respecto al mapa.
        onDrawnRef.current?.(p);
      };

      const ahora = Date.now();
      const transcurrido = ultimaAlMs.current ? ahora - ultimaAlMs.current : 0;
      // Desde donde SE VE, no desde el destino anterior: si el reporte nuevo
      // llega a mitad del tramo, arrancar del destino viejo hacía brincar el
      // marcador hacia adelante.
      const desde = dibujadaRef.current ?? ultimaPosRef.current;

      // ¿Es un movimiento creíble? Se mide entre REPORTES (anterior → nuevo),
      // no desde lo dibujado: el dibujo va atrás del dato, y con dos reportes
      // seguidos (ráfagas del v4-edge) "dibujado → nuevo en 1 s" pasaba de
      // 150 km/h, se tomaba como salto de GPS y el marcador brincaba.
      // Credibilidad con la hora del GPS del equipo, no con la llegada al
      // navegador: los mensajes llegan con retraso variable y una unidad a
      // 95 km/h (1.6 km en 37 s de GPS) "daba" 153 km/h → se tomaba como salto
      // de GPS, brincaba y no se pedía la ruta. Sin hora del equipo, la de
      // llegada.
      const gpsS = Number(vehicle.lastReport?._seconds) || 0;
      const dtGps = gpsS > 0 && ultimaGpsS.current > 0 ? (gpsS - ultimaGpsS.current) * 1000 : 0;
      ultimaGpsS.current = gpsS;
      const rumbo = Number(vehicle.course);
      const recto = !ultimaPosRef.current || esTramoRecto(ultimaPosRef.current, destino, ultimoRumboRef.current, rumbo);
      // En curva: seguirla con la tangente de cada reporte, desde lo dibujado.
      const curva = !recto && desde ? curvaEntre(desde, destino, ultimoRumboRef.current, rumbo) : null;
      // El puntero ya apunta al rumbo NUEVO (llegó con el reporte); en la curva
      // se le resta lo que falta por girar (--giro), así arranca con el rumbo
      // anterior y termina en el nuevo, en vez de ir de lado media vuelta.
      // Variable CSS en el contenedor: sin re-render de React por cuadro.
      const falta = curva ? ((rumbo - (ultimoRumboRef.current ?? rumbo) + 540) % 360) - 180 : 0;
      const giro = (t: number) => container?.style.setProperty("--giro", `${(-falta * (1 - t)).toFixed(1)}deg`);
      giro(curva ? 0 : 1);
      ultimoRumboRef.current = Number.isFinite(rumbo) ? rumbo : null;
      const creible = debeAnimar(ultimaPosRef.current, destino, dtGps > 0 ? dtGps : transcurrido);
      if (desde && creible && (recto || curva)) {
        animacionRef.current = animarHacia(desde, destino, aplicar, {
          duracionMs: duracionPara(transcurrido),
          trayecto: curva ? (t) => (giro(t), curva(t)) : undefined,
        });
      } else {
        // Vuelta cerrada o en U, salto de GPS o hueco: saltar. Deslizar en
        // recta atravesaba la curva por encima de lo que hubiera ("prefiero
        // saltos").
        aplicar(destino);
      }

      ultimaPosRef.current = destino;
      ultimaAlMs.current = ahora;
    }, [vehicle.latitude, vehicle.longitude]);

    useEffect(() => () => animacionRef.current?.cancelar(), []);

    // La unidad seleccionada queda por encima de las vecinas.
    useEffect(() => {
      if (container) container.style.zIndex = isFocused ? "50" : "";
    }, [container, isFocused]);

    if (!container) return null;

    // La etiqueta también enfoca la unidad (el clic sube al onClick del
    // marcador): atinarle al vehículo de lejos era difícil y la etiqueta es lo
    // que se ve. Sin selección de texto: un doble clic la marcaba en azul.
    const label = (bottom: string) =>
      showLabel && (
        <UnitLabel
          settings={unitDisplay}
          name={vehicle.vehicle?.name || vehicle.fullId}
          speed={labelSpeed}
          statusColor={markerColor}
          positionClassName="absolute left-1/2 -translate-x-1/2 z-10"
          style={{ bottom, transition: "bottom 280ms ease-out" }}
        />
      );

    // Marcador de una plataforma anterior (nostalgia): figura + punto.
    if (vehicle.vehicle?.pointerModel === "clasico-v4") {
      return createPortal(
        <div
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          className="relative"
        >
          {label("calc(100% + 16px)")}
          <ClassicV4Marker status={vehicle.status} course={Number(vehicle.course) || 0} size={Math.round(26 * unitDisplay.icon_scale)} />
        </div>,
        container,
      );
    }

    // Modelo de vehículo (puntero estilo Tesla); sin modelo, el clásico.
    if (model) {
      return createPortal(
        <div
          onClick={(e) => {
            e.stopPropagation();
            onClick();
          }}
          // El cuadro del puntero es del tamaño MÁXIMO (se escala hacia abajo):
          // si capturara el mouse, con preventMapHitsAndGesturesFrom se comía la
          // rueda, el arrastre y los clics de lo que tuviera debajo (otras
          // unidades, el mapa). Sólo el vehículo y su área táctil los reciben.
          className="pointer-events-none"
        >
          <VehiclePointer
            model={model}
            status={vehicle.status}
            course={Number(vehicle.course) || 0}
            speed={liveSpeedKmh(vehicle.speed, vehicle.status)}
            focused={isFocused}
            zoom={zoom}
            lat={Number(vehicle.latitude) || 0}
            scaleFactor={unitDisplay.icon_scale}
          >
            {(half) => label(`calc(50% + ${Math.round(half + 7)}px)`)}
          </VehiclePointer>
        </div>,
        container,
      );
    }

    return createPortal(
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        className="cursor-pointer pointer-events-auto"
        style={{
          width: `${markerSize}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Nombre (y velocidad si se mueve) siempre a la vista, como en la
            plataforma anterior: identificar la unidad sin hacer clic. Antes
            era una etiqueta de 10 px gris oscuro que casi no se leía sobre el
            mapa; ahora 12 px oscuro sobre blanco, legible también en
            satélite. Se puede apagar desde Vistas del mapa. */}
        {showLabel && (
          <UnitLabel
            settings={unitDisplay}
            name={vehicle.vehicle?.name || vehicle.fullId}
            speed={labelSpeed}
            statusColor={markerColor}
            positionClassName="absolute bottom-full left-1/2 -translate-x-1/2 mb-[7px]"
          />
        )}
        {vehicle.icon?.includes("http") ? (
          <img
            src={vehicle.icon}
            alt={vehicle.name}
            style={{
              width: `${markerSize}px`,
              height: `${markerSize}px`,
              border: `3px solid ${isFocused ? FOCUS_COLOR : "#ffffff"}`,
              boxShadow: isFocused
                ? `0 0 0 4px ${FOCUS_GLOW}, 0 2px 4px rgba(0, 0, 0, 0.3)`
                : "0 2px 4px rgba(0, 0, 0, 0.3)",
            }}
            className="rounded-full object-cover block"
          />
        ) : markerStyle.hasBorder && markerStyle.clipPath ? (
          // Para "driving" con clipPath: usar dos capas para simular el borde
          <div
            style={{
              position: "relative",
              width: `${markerSize}px`,
              height: `${markerSize}px`,
              transform: markerStyle.rotation
                ? `rotate(${markerStyle.rotation}deg)`
                : undefined,
              filter: isFocused
                ? "drop-shadow(0 4px 10px rgba(0, 0, 0, 0.55)) drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))"
                : "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.3))",
            }}
          >
            {/* Capa exterior (borde) - color cambia instantáneamente */}
            <div
              style={{
                position: "absolute",
                top: 0,
                left: 0,
                width: "100%",
                height: "100%",
                backgroundColor: isFocused ? FOCUS_COLOR : "#ffffff",
                clipPath: markerStyle.clipPath,
              }}
            />
            {/* Capa interior (color del marcador) */}
            <div
              style={{
                position: "absolute",
                top: `${markerStyle.borderWidth}px`,
                left: `${markerStyle.borderWidth}px`,
                width: `calc(100% - ${markerStyle.borderWidth * 2}px)`,
                height: `calc(100% - ${markerStyle.borderWidth * 2}px)`,
                backgroundColor: markerColor,
                clipPath: markerStyle.clipPath,
              }}
            />
          </div>
        ) : (
          // Para otros estados: usar el estilo original con border CSS - grosor fijo
          <div
            style={{
              width: `${markerSize}px`,
              height: `${markerSize}px`,
              backgroundColor: markerColor,
              borderRadius: markerStyle.borderRadius,
              clipPath: markerStyle.clipPath || undefined,
              transform: markerStyle.rotation
                ? `rotate(${markerStyle.rotation}deg)`
                : undefined,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              border: `3px solid ${isFocused ? FOCUS_COLOR : "#ffffff"}`,
              boxShadow: isFocused
                ? `0 0 0 4px ${FOCUS_GLOW}, 0 2px 4px rgba(0, 0, 0, 0.3)`
                : "0 2px 4px rgba(0, 0, 0, 0.3)",
            }}
          >
            {markerStyle.icon && (
              <i className={`${markerStyle.icon} text-white text-sm`}></i>
            )}
          </div>
        )}
      </div>,
      container,
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.vehicle.id === nextProps.vehicle.id &&
      prevProps.vehicle.latitude === nextProps.vehicle.latitude &&
      prevProps.vehicle.longitude === nextProps.vehicle.longitude &&
      prevProps.vehicle.status === nextProps.vehicle.status &&
      prevProps.vehicle.course === nextProps.vehicle.course &&
      prevProps.vehicle.speed === nextProps.vehicle.speed &&
      prevProps.vehicle.vehicle?.name === nextProps.vehicle.vehicle?.name &&
      prevProps.showLabel === nextProps.showLabel &&
      prevProps.zoom === nextProps.zoom &&
      prevProps.vehicle.vehicle?.pointerModel === nextProps.vehicle.vehicle?.pointerModel &&
      prevProps.vehicle.vehicle?.bodyType === nextProps.vehicle.vehicle?.bodyType &&
      !!prevProps.onDrawn === !!nextProps.onDrawn &&
      prevProps.isFocused === nextProps.isFocused &&
      prevProps.map === nextProps.map
    );
  },
);

// Tamaño fijo del cluster
const CLUSTER_SIZE = 40;

const unitsKey = (vehicles: any[] = []) =>
  vehicles.map((v) => `${v.id}:${Math.round(Number(v.speed) || 0)}:${Math.round(Number(v.course) || 0)}`).join("|");

// Componente ClusteredMarker usando google.maps.OverlayView nativo para evitar parpadeo
const ClusteredMarker = memo(
  ({
    cluster,
    onClick,
    isFocused,
    map,
  }: {
    cluster: any;
    onClick: () => void;
    isFocused: boolean;
    map: google.maps.Map | null;
  }) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);

    const { t } = useTranslation("lite");
    const segments = useMemo(
      () => clusterSegments(cluster?.vehicles || [], VEHICLE_STATUS_COLORS),
      [cluster?.vehicles],
    );
    const unitList = useMemo(() => {
      const { rows, more } = clusterUnitRows(cluster?.vehicles || []);
      return {
        more,
        rows: rows.map(({ speed, ...r }) => ({
          ...r,
          color: VEHICLE_STATUS_COLORS[r.status] ?? "#6B7280",
          speedText: rowSpeed(liveSpeedKmh(speed, r.status)),
        })),
      };
    }, [cluster?.vehicles]);

    useEffect(() => {
      if (!map || !cluster?.center) return;

      const div = document.createElement("div");
      div.style.position = "absolute";
      // Por encima de las unidades sueltas (que van en la misma capa y sin
      // z-index): sus etiquetas tapaban el número del grupo (captura de
      // Daniel, 21-oct-2026, «264 VICTOR VARA» sobre un grupo en Navojoa). La
      // unidad seleccionada usa 50 y sigue quedando arriba.
      div.style.zIndex = "40";

      class ClusterOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) {
            panes.overlayMouseTarget.appendChild(this.div);
            // Que el clic sobre el overlay no llegue también al mapa: sin esto
            // tocar una unidad era además "clic en el mapa", que suelta el
            // seguimiento; seleccionar no funcionaba (video 6-oct-2026).
            google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.div);
          }
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;

          const pos = projection.fromLatLngToDivPixel(this.position);
          if (pos) {
            // Centrado por su propio tamaño: con 3 dígitos la ficha pasa de
            // CLUSTER_SIZE y un offset fijo la corría a la derecha.
            this.div.style.transform = "translate(-50%, -50%)";
            this.div.style.left = `${pos.x}px`;
            this.div.style.top = `${pos.y}px`;
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }

        updatePosition(newPosition: google.maps.LatLng) {
          this.position = newPosition;
          this.draw();
        }
      }

      const position = new google.maps.LatLng(
        Number(cluster.center.lat),
        Number(cluster.center.lng),
      );

      const overlay = new ClusterOverlay(position, div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
        setContainer(null);
      };
    }, [map, cluster?.id]);

    // Actualizar posición cuando cambian las coordenadas
    useEffect(() => {
      if (overlayRef.current && cluster?.center) {
        const newPosition = new google.maps.LatLng(
          Number(cluster.center.lat),
          Number(cluster.center.lng),
        );
        (overlayRef.current as any).updatePosition(newPosition);
      }
    }, [cluster?.center?.lat, cluster?.center?.lng]);

    if (!cluster || !cluster.center || cluster.count <= 0 || !container) {
      return null;
    }

    const total = cluster.count || 0;
    const moving = segments.some((seg) => seg.status === "driving");

    return createPortal(
      <div
        onClick={onClick}
        // La tarjeta de unidades sale hacia la derecha y el grupo vecino (mismo
        // z-index 40, agregado después) la tapaba: el grupo con el mouse sube.
        onMouseEnter={() => (container.style.zIndex = "45")}
        onMouseLeave={() => (container.style.zIndex = "40")}
        className={`group relative cursor-pointer transition-transform duration-200 ease-out motion-reduce:transition-none ${
          isFocused ? "scale-125" : "hover:-translate-y-0.5 hover:scale-105"
        }`}
        style={{ minWidth: `${CLUSTER_SIZE}px`, height: `${CLUSTER_SIZE}px` }}
      >
        <ClusterTile
          count={total}
          segments={segments}
          movingColor={moving ? VEHICLE_STATUS_COLORS.driving : undefined}
          rows={unitList.rows}
          more={unitList.more}
          moreLabel={(n) => t("map.clusterMore", { count: n, defaultValue: "+{{count}} más · clic para acercar" })}
        />
      </div>,
      container,
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.cluster?.id === nextProps.cluster?.id &&
      prevProps.cluster?.center?.lat === nextProps.cluster?.center?.lat &&
      prevProps.cluster?.center?.lng === nextProps.cluster?.center?.lng &&
      prevProps.cluster?.count === nextProps.cluster?.count &&
      // Sin esto la barra no cambiaba cuando una unidad del grupo pasaba de
      // detenida a conduciendo: el total era el mismo.
      segmentsKey(clusterSegments(prevProps.cluster?.vehicles || [], VEHICLE_STATUS_COLORS)) ===
        segmentsKey(clusterSegments(nextProps.cluster?.vehicles || [], VEHICLE_STATUS_COLORS)) &&
      // La tarjeta de unidades muestra velocidad y rumbo: si sólo cambiaba eso,
      // seguía con los de cuando se formó el grupo.
      unitsKey(prevProps.cluster?.vehicles) === unitsKey(nextProps.cluster?.vehicles) &&
      prevProps.isFocused === nextProps.isFocused &&
      prevProps.map === nextProps.map
    );
  },
);

// Tamaño fijo del marcador de punto de interés
const INTEREST_POINT_SIZE = 32;
const INTEREST_POINT_LABEL_HEIGHT = 22;
const INTEREST_POINT_LABEL_MARGIN = 2;

// Offset constante para InterestPointMarker - el punto de anclaje es el centro inferior del círculo
const INTEREST_POINT_OFFSET = {
  x: -(INTEREST_POINT_SIZE / 2), // Centrar horizontalmente
  y: -(
    INTEREST_POINT_SIZE +
    INTEREST_POINT_LABEL_HEIGHT +
    INTEREST_POINT_LABEL_MARGIN
  ), // Altura total: label + margin + marcador
};

// Componente InterestPointMarker usando google.maps.OverlayView nativo para evitar parpadeo
const InterestPointMarker = memo(
  ({
    point,
    onClick,
    isSelected,
    map,
    isDarkMode,
  }: {
    point: PointOfInterest;
    onClick: () => void;
    isSelected: boolean;
    map: google.maps.Map | null;
    isDarkMode: boolean;
  }) => {
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);

    useEffect(() => {
      if (!map) return;

      const div = document.createElement("div");
      div.style.position = "absolute";
      div.style.cursor = "pointer";
      div.style.pointerEvents = "auto";

      class InterestPointOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) {
            panes.overlayMouseTarget.appendChild(this.div);
            // Que el clic sobre el overlay no llegue también al mapa: sin esto
            // tocar una unidad era además "clic en el mapa", que suelta el
            // seguimiento; seleccionar no funcionaba (video 6-oct-2026).
            google.maps.OverlayView.preventMapHitsAndGesturesFrom(this.div);
          }
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;

          const pos = projection.fromLatLngToDivPixel(this.position);
          if (pos) {
            this.div.style.left = `${pos.x + INTEREST_POINT_OFFSET.x}px`;
            this.div.style.top = `${pos.y + INTEREST_POINT_OFFSET.y}px`;
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }

        updatePosition(newPosition: google.maps.LatLng) {
          this.position = newPosition;
          this.draw();
        }
      }

      const position = new google.maps.LatLng(
        Number(point.location.latitude),
        Number(point.location.longitude),
      );

      const overlay = new InterestPointOverlay(position, div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
        setContainer(null);
      };
    }, [map, point.id]);

    // Actualizar posición cuando cambian las coordenadas
    useEffect(() => {
      if (
        overlayRef.current &&
        point.location?.latitude &&
        point.location?.longitude
      ) {
        const newPosition = new google.maps.LatLng(
          Number(point.location.latitude),
          Number(point.location.longitude),
        );
        (overlayRef.current as any).updatePosition(newPosition);
      }
    }, [point.location?.latitude, point.location?.longitude]);

    if (!container) return null;

    return createPortal(
      <div
        onClick={(e) => {
          e.stopPropagation();
          onClick();
        }}
        style={{
          width: `${INTEREST_POINT_SIZE}px`,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
        }}
      >
        {/* Label con nombre */}
        <div
          className={`px-1.5 py-0.5 text-[10px] rounded shadow-lg whitespace-nowrap ${isDarkMode ? "bg-gray-900 text-white" : "bg-gray-900 text-white"
            }`}
          style={{ marginBottom: "2px" }}
          title={point.name}
        >
          {point.name.length > 12
            ? `${point.name.slice(0, 12)}...`
            : point.name}
        </div>
        {/* Marcador - Azul si es compartido, Rojo si es privado */}
        <div
          style={{
            width: `${INTEREST_POINT_SIZE}px`,
            height: `${INTEREST_POINT_SIZE}px`,
            backgroundColor: point.shared ? "#3B82F6" : "#EF4444",
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            border: `3px solid ${isSelected ? brand.primary : "#ffffff"}`,
            boxShadow: isSelected
              ? `0 0 0 4px ${point.shared ? "rgba(59, 130, 246, 0.3)" : "rgba(239, 68, 68, 0.3)"}, 0 2px 4px rgba(0, 0, 0, 0.3)`
              : "0 2px 4px rgba(0, 0, 0, 0.3)",
          }}
        >
          <i
            className={`${point.shared ? "ri-map-pin-2-fill" : "ri-map-pin-user-fill"} text-white text-sm`}
          ></i>
        </div>
      </div>,
      container,
    );
  },
  (prevProps, nextProps) => {
    return (
      prevProps.point.id === nextProps.point.id &&
      prevProps.point.location?.latitude ===
      nextProps.point.location?.latitude &&
      prevProps.point.location?.longitude ===
      nextProps.point.location?.longitude &&
      prevProps.point.name === nextProps.point.name &&
      prevProps.point.shared === nextProps.point.shared &&
      prevProps.isSelected === nextProps.isSelected &&
      prevProps.isDarkMode === nextProps.isDarkMode &&
      prevProps.map === nextProps.map
    );
  },
);

// Componente para mostrar información del punto de interés (popup estilo Google Maps)
const InterestPointInfoPopup = memo(
  ({
    point,
    onClose,
    map,
    isDarkMode,
  }: {
    point: PointOfInterest;
    onClose: () => void;
    map: google.maps.Map | null;
    isDarkMode: boolean;
  }) => {
    const { t } = useTranslation("common");
    const [container, setContainer] = useState<HTMLDivElement | null>(null);
    const overlayRef = useRef<google.maps.OverlayView | null>(null);
    const statuses = useStatusesStore((state) => state.statuses);

    // Obtener el status del POI desde el store
    const poiStatus = useMemo(() => {
      return statuses.find((s) => s.id === point.status_id);
    }, [statuses, point.status_id]);

    useEffect(() => {
      if (!map) return;

      const div = document.createElement("div");
      div.style.position = "absolute";
      div.style.zIndex = "1000";
      div.style.pointerEvents = "auto";

      class InfoPopupOverlay extends google.maps.OverlayView {
        private position: google.maps.LatLng;
        private div: HTMLDivElement;

        constructor(position: google.maps.LatLng, div: HTMLDivElement) {
          super();
          this.position = position;
          this.div = div;
        }

        onAdd() {
          const panes = this.getPanes();
          if (panes) {
            panes.floatPane.appendChild(this.div);
          }
        }

        draw() {
          const projection = this.getProjection();
          if (!projection) return;

          const pos = projection.fromLatLngToDivPixel(this.position);
          if (pos) {
            // Posicionar el popup encima del marcador
            const offsetY = -(
              INTEREST_POINT_SIZE +
              INTEREST_POINT_LABEL_HEIGHT +
              20
            );
            this.div.style.left = `${pos.x}px`;
            this.div.style.top = `${pos.y + offsetY}px`;
            this.div.style.transform = "translate(-50%, -100%)";
          }
        }

        onRemove() {
          if (this.div.parentNode) {
            this.div.parentNode.removeChild(this.div);
          }
        }
      }

      const position = new google.maps.LatLng(
        Number(point.location.latitude),
        Number(point.location.longitude),
      );

      const overlay = new InfoPopupOverlay(position, div);
      overlay.setMap(map);
      overlayRef.current = overlay;
      setContainer(div);

      return () => {
        if (overlayRef.current) {
          overlayRef.current.setMap(null);
          overlayRef.current = null;
        }
        setContainer(null);
      };
    }, [map, point.id, point.location?.latitude, point.location?.longitude]);

    if (!container) return null;

    const getStatusInfo = () => {
      const statusName = poiStatus?.name?.toLowerCase() || "";
      if (statusName.includes("activ") && !statusName.includes("inactiv")) {
        return {
          label: "Activo",
          color: "text-green-500",
          bg: "bg-green-500/10",
        };
      }
      if (statusName.includes("inactiv")) {
        return {
          label: "Inactivo",
          color: "text-red-500",
          bg: "bg-red-500/10",
        };
      }
      return {
        label: poiStatus?.name || "Sin estado",
        color: "text-gray-500",
        bg: "bg-gray-500/10",
      };
    };

    const statusInfo = getStatusInfo();

    return createPortal(
      <div
        onClick={(e) => e.stopPropagation()}
        className={`rounded-xl shadow-2xl border overflow-hidden min-w-[240px] max-w-[280px] ${isDarkMode
            ? "bg-dark-card border-dark-border"
            : "bg-white border-gray-200"
          }`}
        style={{ zIndex: 1000 }}
      >
        {/* Header - Azul si es compartido, Rojo si es privado */}
        <div
          className={`px-3 py-2.5 flex items-center justify-between ${isDarkMode
              ? "bg-dark-surface border-b border-dark-border"
              : point.shared
                ? "bg-blue-50 border-b border-blue-100"
                : "bg-red-50 border-b border-red-100"
            }`}
        >
          <div className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center ${point.shared ? "bg-blue-500" : "bg-red-500"}`}
            >
              <i
                className={`${point.shared ? "ri-map-pin-2-fill" : "ri-map-pin-user-fill"} text-white text-sm`}
              ></i>
            </div>
            <div>
              <h3
                className={`font-semibold text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {point.name}
              </h3>
              <span
                className={`text-xs px-2 py-0.5 rounded-full ${statusInfo.bg} ${statusInfo.color}`}
              >
                {statusInfo.label}
              </span>
            </div>
          </div>
          <button
            aria-label={t("general.close")}
            onClick={onClose}
            className={`w-7 h-7 rounded-full flex items-center justify-center transition-colors ${isDarkMode
                ? "hover:bg-dark-border text-gray-400"
                : "hover:bg-gray-200 text-gray-500"
              }`}
          >
            <i className="ri-close-line text-lg"></i>
          </button>
        </div>

        {/* Content */}
        <div className="p-3 space-y-2.5">
          {/* Dirección */}
          {point.address && (
            <div className="flex items-start gap-2">
              <i
                className={`ri-map-pin-line text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              ></i>
              <div>
                <p
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Dirección
                </p>
                <p
                  className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {point.address}
                </p>
              </div>
            </div>
          )}

          {/* Coordenadas */}
          <div className="flex items-start gap-2">
            <i
              className={`ri-focus-3-line text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            ></i>
            <div>
              <p
                className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              >
                Coordenadas
              </p>
              <p
                className={`text-sm font-mono ${isDarkMode ? "text-white" : "text-gray-900"}`}
              >
                {point.location.latitude.toFixed(6)},{" "}
                {point.location.longitude.toFixed(6)}
              </p>
            </div>
          </div>

          {/* Categoría */}
          {point.category && (
            <div className="flex items-start gap-2">
              <i
                className={`ri-price-tag-3-line text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              ></i>
              <div>
                <p
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Categoría
                </p>
                <p
                  className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {point.category.name}
                </p>
              </div>
            </div>
          )}

          {/* Notas */}
          {point.notes && (
            <div className="flex items-start gap-2">
              <i
                className={`ri-file-text-line text-sm mt-0.5 ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
              ></i>
              <div>
                <p
                  className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
                >
                  Notas
                </p>
                <p
                  className={`text-sm ${isDarkMode ? "text-white" : "text-gray-900"}`}
                >
                  {point.notes}
                </p>
              </div>
            </div>
          )}

          {/* Compartido */}
          <div className="flex items-center gap-2 pt-2 border-t border-dashed ${isDarkMode ? 'border-dark-border' : 'border-gray-200'}">
            <i
              className={`ri-${point.shared ? "group" : "user"}-line text-sm ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            ></i>
            <span
              className={`text-xs ${isDarkMode ? "text-gray-400" : "text-gray-500"}`}
            >
              {point.shared ? "Compartido con la compañía" : "Privado"}
            </span>
          </div>
        </div>

        {/* Footer con flecha */}
        <div className="relative">
          <div
            className={`absolute left-1/2 -bottom-2 w-4 h-4 transform -translate-x-1/2 rotate-45 ${isDarkMode
                ? "bg-dark-card border-r border-b border-dark-border"
                : "bg-white border-r border-b border-gray-200"
              }`}
          ></div>
        </div>
      </div>,
      container,
    );
  },
);

const getShiftedCenter = (
  map: google.maps.Map,
  lat: number,
  lng: number,
  offsetX: number,
): Punto => {
  const projection = map.getProjection();
  const zoom = map.getZoom();
  if (!offsetX || !projection || zoom == null) return { lat, lng };
  const worldPoint = projection.fromLatLngToPoint(
    new google.maps.LatLng(lat, lng),
  );
  if (!worldPoint) return { lat, lng };
  const shifted = projection.fromPointToLatLng(
    new google.maps.Point(
      worldPoint.x - offsetX / Math.pow(2, zoom),
      worldPoint.y,
    ),
  );
  return shifted ? { lat: shifted.lat(), lng: shifted.lng() } : { lat, lng };
};

// Zoom escalonado: el mapa es ráster (sin mapId), sólo admite niveles enteros,
// y un setZoom de 5 → 16 de un golpe se sentía como un salto. Se avanza un
// nivel por paso y cada paso usa la animación propia de Google (~250 ms).
// Pasos de 200 ms: con 120 se encimaban con esa animación y el zoom se sentía
// agresivo. Mientras dura un paso, la cámara de seguimiento no mueve el
// centro (ver onFollowedDrawn): un moveCamera a media animación la corta y
// el zoom "rebotaba". Pedidos nuevos sólo mueven el destino. Con "reducir
// movimiento", directo.
const ZOOM_STEP_MS = 200;
const ZOOM_ANIM_MS = 300;
const zoomSteps = new WeakMap<google.maps.Map, { target: number; timer: number | null; busyUntil: number; left?: number }>();
const zoomTo = (map: google.maps.Map, target: number) => {
  // Dentro de los límites REALES del mapa (minZoom 4): con un destino de 2,
  // Google recortaba cada paso, el escalonado "nunca llegaba" y reintentaba
  // cada 200 ms para siempre (el mapa se trababa y rebotaba, también después
  // de soltar la unidad).
  const min = Number(map.get("minZoom")) || 4;
  const max = Number(map.get("maxZoom")) || 22;
  const t = Math.max(min, Math.min(max, Math.round(target)));
  if (window.matchMedia?.("(prefers-reduced-motion: reduce)").matches) {
    map.setZoom(t);
    return;
  }
  const st = zoomSteps.get(map) ?? { target: t, timer: null, busyUntil: 0 };
  st.target = t;
  zoomSteps.set(map, st);
  // Nuevo destino a medio vuelo (rueda siguiendo): rehacer el tope.
  const budget = () => (st.left = Math.abs(t - Math.round(map.getZoom() ?? t)) + 2);
  if (st.timer != null) return void budget();
  // Salto largo (p. ej. 6 → 19): ir directo a 2 niveles del destino y animar
  // sólo esos 2. Encadenar 13 animaciones mostraba mosaicos viejos escalados
  // (letras gigantes borrosas) mientras cargaban los nuevos.
  const z0 = Math.round(map.getZoom() ?? t);
  if (Math.abs(t - z0) > 3) map.setZoom(t - 2 * Math.sign(t - z0));
  // Tope de pasos: los niveles que faltan + 2. Si Google recorta (p. ej. el
  // tope del satélite) el vuelo se agota en vez de reintentar cada 200 ms
  // para siempre (el rebote de #748).
  budget();
  const step = () => {
    const z = Math.round(map.getZoom() ?? st.target);
    if (z === st.target) {
      st.timer = null;
      return;
    }
    // No se mide "¿avanzó?" justo tras setZoom: con una animación en curso
    // getZoom() aún devolvía el valor viejo y el vuelo se cortaba (quedaba en
    // 17 en vez de 19, o ni se movía de 9).
    if ((st.left = (st.left ?? 0) - 1) < 0) {
      st.target = z;
      st.timer = null;
      return;
    }
    map.setZoom(z + Math.sign(st.target - z));
    st.busyUntil = Date.now() + ZOOM_ANIM_MS;
    st.timer = window.setTimeout(step, ZOOM_STEP_MS);
  };
  step();
};
// Destino en curso (si hay un vuelo de zoom a medias) o el zoom actual.
const zoomTarget = (map: google.maps.Map) => zoomSteps.get(map)?.timer != null ? zoomSteps.get(map)!.target : Math.round(map.getZoom() ?? DEFAULT_ZOOM);

export default function MapView() {
  const { t } = useTranslation("lite");
  const { isDarkMode } = useDarkMode();
  const { ready: mapReady, status: mapReadyStatus } = useMapReady();

  const context = useOutletContext<OutletContextType>();
  const showGrid = context?.showGrid ?? false;
  const mapType = context?.mapType ?? "roadmap";
  const showTraffic = context?.showTraffic ?? false;
  const showTransit = context?.showTransit ?? false;
  const showBicycling = context?.showBicycling ?? false;
  const showGeofences = context?.showGeofences ?? false;
  const geofenceFilter = context?.geofenceFilter ?? "all";
  const showInterestPoints = context?.showInterestPoints ?? false;
  const showRoutes = context?.showRoutes ?? false;
  const showRuler = context?.showRuler ?? false;
  const showIcons = context?.showIcons ?? true;
  const showVehicleLabels = context?.showVehicleLabels ?? true;
  const [selectedUnit, setSelectedUnit] = useState<any>(null);
  // CustomMarker está memorizado y su onClick puede ser de un render viejo:
  // la ficha abierta se lee de acá, no del cierre.
  // Ficha minimizada a una pastilla en su misma esquina (no cerrada).
  const [cardMinimized, setCardMinimized] = useState(false);
  // Si la ficha se abrió desde la pastilla, crece DESDE ella (y al minimizar
  // vuelve a ella): lo que se va por un lado regresa por el mismo.
  const [cardFromPill, setCardFromPill] = useState(false);
  const selectedUnitIdRef = useRef<string | null>(null);
  selectedUnitIdRef.current = selectedUnit?.id ?? null;
  const cardMinimizedRef = useRef(false);
  cardMinimizedRef.current = cardMinimized;
  const [selectedInterestPoint, setSelectedInterestPoint] =
    useState<PointOfInterest | null>(null);
  const [h3Grids, setH3Grids] = useState<any[]>([]);
  const [currentZoom, setCurrentZoom] = useState<number>(DEFAULT_ZOOM);
  // Zoom de los punteros de vehículo: se actualiza al EMPEZAR cada cambio de
  // zoom (zoom_changed) para que crezcan junto con la animación de Google;
  // currentZoom se actualiza al terminar (idle) y llegaba tarde.
  const [markerZoom, setMarkerZoom] = useState<number>(DEFAULT_ZOOM);
  const [isGeneratingGrids, setIsGeneratingGrids] = useState(false);
  const [dismissedLoadError, setDismissedLoadError] = useState<DevicesLoadErrorCode | null>(null);
  const [mapInstance, setMapInstance] = useState<google.maps.Map | null>(null);
  const mapRef = useRef<google.maps.Map | null>(null);
  const generationTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ruler state
  const [rulerPoints, setRulerPoints] = useState<
    { lat: number; lng: number }[]
  >([]);
  const [isRulerCollapsed, setIsRulerCollapsed] = useState(false);
  const polylineRef = useRef<google.maps.Polyline | null>(null);

  // Search state from context
  const showSearchBox = context?.showSearchBox ?? false;
  const setShowSearchBox = context?.setShowSearchBox ?? (() => { });

  const { isLoaded, loadError } = useJsApiLoader({
    googleMapsApiKey: import.meta.env.VITE_GOOGLE_MAPS_API_KEY,
    libraries: GOOGLE_MAPS_LIBRARIES,
  });

  const vehicles = useFilteredSocketVehicles();
  const devicesLoadError = useVehiclesSocketStore((state) => state.loadError);
  const requestReload = useVehiclesSocketStore((state) => state.requestReload);

  const focusedVehicleId = useVehiclesSocketStore(
    (state) => state.focusedVehicleId,
  );
  const toggleDriveView = useDriveView((s) => s.toggle);
  const driveViewOpenId = useDriveView((s) => s.vehicleId);
  const setFocusedVehicleId = useVehiclesSocketStore(
    (state) => state.setFocusedVehicleId,
  );
  const liveDataDeviceId = useLiveDataPanelStore((state) => state.deviceId);
  const requestAddressForDevice = useVehiclesSocketStore(
    (state) => state.requestAddressForDevice,
  );
  const geofencesQuery = useGeofencesList(
    { limit: 1000 },
    { enabled: showGeofences },
  );
  const geofences = geofencesQuery.data?.data ?? [];
  const statuses = useStatusesStore((state) => state.statuses);
  const poiQuery = usePointsInterestList(
    { limit: 100 },
    { enabled: showInterestPoints },
  );
  const pointsOfInterest = poiQuery.data?.data ?? [];
  const routesQuery = useRoutesList(
    { limit: 100 },
    { enabled: showRoutes },
  );
  const routes = routesQuery.data?.data ?? [];
  const user = useUserStore((state) => state.user);
  const selectedCompany = useVehiclesSocketStore(
    (state) => state.selectedCompany,
  );

  // Verificar si es usuario reseller (ADMIN_RESELLER_ROLE_ID = "Ne6pwRmFkr4bArJN47NZ")
  const isReseller = useMemo(() => {
    const userRoleId = localStorage.getItem("userRoleId") || "";
    return userRoleId === "Ne6pwRmFkr4bArJN47NZ";
  }, []);

  // Filtrar puntos de interés según el tipo de usuario
  // - Usuario normal: ver sus puntos + compartidos de su compañía
  // - Usuario reseller con compañía seleccionada: ver todos los puntos de esa compañía (propios + compartidos)
  // - Usuario reseller sin compañía seleccionada: no mostrar puntos
  const filteredPointsOfInterest = useMemo(() => {
    if (!user) return [];

    // Si es reseller
    if (isReseller) {
      // Si no hay compañía seleccionada, no mostrar puntos
      if (!selectedCompany) return [];

      // Mostrar todos los puntos de la compañía seleccionada (propios del usuario de esa compañía + compartidos)
      return pointsOfInterest.filter((point) => {
        // Mostrar puntos que pertenecen a la compañía seleccionada
        if (point.company_id === selectedCompany) {
          // Mostrar si es compartido o si el reseller lo creó
          return point.shared || point.createdBy === user.id;
        }
        return false;
      });
    }

    // Usuario normal: ver sus puntos + compartidos de su misma compañía
    return pointsOfInterest.filter((point) => {
      // Si el punto fue creado por el usuario, mostrarlo
      if (point.createdBy === user.id) return true;

      // Si el punto es compartido y pertenece a la misma compañía, mostrarlo
      if (point.shared && point.company_id === user.company_id) return true;

      return false;
    });
  }, [pointsOfInterest, user, isReseller, selectedCompany]);

  // Helper to check if a geofence is active based on status_id
  const isGeofenceActive = useCallback(
    (statusId: string) => {
      const status = statuses.find((s) => s.id === statusId);
      const statusName = status?.name?.toLowerCase() || "";
      return statusName.includes("activ");
    },
    [statuses],
  );

  const activeTrackingsRecord = useVehicleRouteTrackingStore(
    (state) => state.activeTrackings,
  );

  const activeTrackings = useMemo(() => {
    return Object.values(activeTrackingsRecord).map((t: VehicleTracking) => {
      const { segments, stateMarkers } = buildRouteSegments(t.points);
      return {
        vehicleId: t.vehicleId,
        color: t.color,
        points: t.points,
        startPoint: t.startPoint,
        endPoint: t.endPoint,
        segments,
        stateMarkers,
      };
    });
  }, [activeTrackingsRecord]);

  // Footprint tracking store
  const activeFootprintsRecord = useVehicleFootprintTrackingStore(
    (state) => state.activeFootprints,
  );

  const activeFootprints = useMemo(() => {
    return Object.values(activeFootprintsRecord).map((f: VehicleFootprint) => ({
      vehicleId: f.vehicleId,
      color: f.color,
      points: f.points,
      startPoint: f.startPoint,
    }));
  }, [activeFootprintsRecord]);

  // Map de vehiculos por ID para lookups O(1) en banderas
  const vehiclesById = useMemo(() => {
    const map = new Map<string, (typeof vehicles)[0]>();
    for (const v of vehicles) {
      map.set(v.id, v);
    }
    return map;
  }, [vehicles]);

  const routesToRender = useMemo(
    () => (showRoutes ? routes : []),
    [showRoutes, routes],
  );

  // Stable path arrays per route id. The reference must be stable across
  // renders so RouteDirectionsPolyline doesn't re-request directions every
  // time another piece of map state changes.
  const routePaths = useMemo(() => {
    const out: Array<{
      id: string;
      points: Array<{ lat: number; lng: number }>;
      axis: Array<{ lat: number; lng: number }> | null;
      buffer: number;
      firstPointRadius: number;
      lastPointRadius: number;
    }> = [];
    for (const route of routesToRender) {
      const coords = route.principal_route;
      if (!coords || coords.length < 2) continue;
      const dense = Array.isArray(route.principal_route_dense)
        ? route.principal_route_dense
        : [];
      out.push({
        id: route.id,
        points: coords.map((c) => ({ lat: c.latitud, lng: c.longitud })),
        axis:
          dense.length >= 2
            ? dense.map((c) => ({ lat: c.latitude, lng: c.longitude }))
            : null,
        buffer: route.principal_route_buffer ?? LINEAR_BUFFER_DEFAULT,
        firstPointRadius: route.first_point_radius ?? 0,
        lastPointRadius: route.last_point_radius ?? 0,
      });
    }
    return out;
  }, [routesToRender]);

  useDevicesFilterDiagnostics(vehicles?.length ?? 0);

  useEffect(() => {
    if (new URLSearchParams(window.location.search).get("testError") === "true") {
      useVehiclesSocketStore.getState().setLoadError("API_UNREACHABLE");
    }
  }, []);

  useEffect(() => {
    if (!devicesLoadError) setDismissedLoadError(null);
  }, [devicesLoadError]);

  const visibleLoadError =
    devicesLoadError && devicesLoadError !== dismissedLoadError ? devicesLoadError : null;
  const loadErrorConfig = visibleLoadError ? DEVICES_LOAD_ERRORS[visibleLoadError] : null;

  const handleRetryConnection = () => {
    if (!devicesLoadError) return;
    trackDiagnostic("devices_retry_clicked", { code: devicesLoadError });
    if (devicesLoadError === "SOCKET_GAVE_UP") {
      connectSocket();
      return;
    }
    requestReload();
  };

  // Clear ruler points when ruler is disabled
  useEffect(() => {
    if (!showRuler) {
      setRulerPoints([]);
      // Clean up polyline
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    }
  }, [showRuler]);

  // Manage polyline when ruler points change
  useEffect(() => {
    if (!mapRef.current || !showRuler) return;

    // Clean up existing polyline
    if (polylineRef.current) {
      polylineRef.current.setMap(null);
      polylineRef.current = null;
    }

    // Create new polyline if we have 2 or more points
    if (rulerPoints.length >= 2) {
      polylineRef.current = new google.maps.Polyline({
        path: rulerPoints,
        strokeColor: brand.primary,
        strokeOpacity: 1,
        strokeWeight: 3,
        zIndex: 1000,
        map: mapRef.current,
      });
    }

    // Cleanup function
    return () => {
      if (polylineRef.current) {
        polylineRef.current.setMap(null);
        polylineRef.current = null;
      }
    };
  }, [rulerPoints, showRuler]);

  // Function to calculate distance between two points using Haversine formula
  const calculateDistance = useCallback(
    (
      point1: { lat: number; lng: number },
      point2: { lat: number; lng: number },
    ): number => {
      // En km, como el resto de la plataforma (reportes, kilometraje): la regla
      // medía en millas y pies, unidades que no usa ninguna flota en México.
      const R = 6371;
      const dLat = ((point2.lat - point1.lat) * Math.PI) / 180;
      const dLon = ((point2.lng - point1.lng) * Math.PI) / 180;
      const a =
        Math.sin(dLat / 2) * Math.sin(dLat / 2) +
        Math.cos((point1.lat * Math.PI) / 180) *
        Math.cos((point2.lat * Math.PI) / 180) *
        Math.sin(dLon / 2) *
        Math.sin(dLon / 2);
      const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
      return R * c; // km
    },
    [],
  );

  // Function to calculate total accumulated distance through all points
  const calculateTotalDistance = useCallback(
    (points: { lat: number; lng: number }[]): number => {
      if (points.length < 2) return 0;

      let totalDistance = 0;
      for (let i = 0; i < points.length - 1; i++) {
        totalDistance += calculateDistance(points[i], points[i + 1]);
      }
      return totalDistance;
    },
    [calculateDistance],
  );

  // Handle map click for ruler
  const handleMapClick = useCallback(
    (e: google.maps.MapMouseEvent) => {
      // Clic en el mapa (fuera de un marcador) suelta la unidad seguida: más
      // fácil de descubrir que arrastrar. La ficha se minimiza a la píldora
      // (no se cierra): despeja el mapa y se recupera con un toque. Sin
      // ficha abierta el evento no hace nada.
      if (!showRuler) {
        const store = useVehiclesSocketStore.getState();
        if (store.focusedVehicleId) store.setFocusedVehicleId(null);
        window.dispatchEvent(new Event(MINIMIZE_EVENT));
        return;
      }
      if (!e.latLng) return;

      const clickedPoint = {
        lat: e.latLng.lat(),
        lng: e.latLng.lng(),
      };

      // Allow unlimited points - just add new point on each click
      setRulerPoints((prev) => [...prev, clickedPoint]);
    },
    [showRuler],
  );

  const getH3Resolution = useCallback((zoom: number): number => {
    const normalizedZoom = Math.floor(zoom);
    return (
      H3_ZOOM_MAPPING[normalizedZoom] ||
      H3_ZOOM_MAPPING[Math.max(0, Math.min(normalizedZoom, 30))] ||
      7
    );
  }, []);

  const generateH3Grids = useCallback(
    (map: google.maps.Map, force = false) => {
      if (!map || isGeneratingGrids) return;

      const bounds = map.getBounds();
      if (!bounds) return;

      // Usar el zoom actual del mapa en lugar del estado para asegurar sincronización
      const mapZoom = map.getZoom() || DEFAULT_ZOOM;
      const resolution = getH3Resolution(mapZoom);

      // Verificar si ya tenemos grids para esta resolución y no forzar regeneración
      if (
        !force &&
        h3Grids.length > 0 &&
        h3Grids.every((grid) => grid.resolution === resolution)
      ) {
        // También verificar si el zoom actual coincide con el zoom de las grids
        const currentMapZoom = map.getZoom() || DEFAULT_ZOOM;
        const expectedResolution = getH3Resolution(currentMapZoom);
        if (
          h3Grids.length > 0 &&
          h3Grids.every((grid) => grid.resolution === expectedResolution)
        ) {
          return;
        }
      }

      setIsGeneratingGrids(true);

      try {
        const ne = bounds.getNorthEast();
        const sw = bounds.getSouthWest();

        // Generar celdas H3 para cubrir completamente el bounds visible
        const grids: any[] = [];
        const processedCells = new Set<string>();

        // Función para obtener todas las celdas en un área rectangular
        const getCellsInBounds = (
          minLat: number,
          minLng: number,
          maxLat: number,
          maxLng: number,
          resolution: number,
        ) => {
          const cells = new Set<string>();

          // Calcular paso basado en la resolución - pasos mucho más pequeños para mejor cobertura
          let stepSize;
          if (resolution === 0) {
            stepSize = 1.0; // Resolución 0 -> paso muy grande
          } else if (resolution === 1) {
            stepSize = 0.15; // Reducido aún más de 0.3 a 0.15 para zoom 3
          } else if (resolution === 2) {
            stepSize = 0.2;
          } else if (resolution === 3) {
            stepSize = 0.08; // Reducido de 0.1 a 0.08 para mejor cobertura
          } else if (resolution === 4) {
            stepSize = 0.05;
          } else if (resolution === 5) {
            stepSize = 0.02;
          } else if (resolution === 6) {
            stepSize = 0.01;
          } else if (resolution === 7) {
            stepSize = 0.005;
          } else if (resolution === 8) {
            stepSize = 0.002;
          } else if (resolution === 9) {
            stepSize = 0.001;
          } else if (resolution === 10) {
            stepSize = 0.0005;
          } else if (resolution === 11) {
            stepSize = 0.0002;
          } else if (resolution === 12) {
            stepSize = 0.0001;
          } else if (resolution === 13) {
            stepSize = 0.00005;
          } else {
            stepSize = 0.00001; // Resolución 14 -> paso muy pequeño
          }

          // Limitar el número de muestras para mantener rendimiento
          const latRange = maxLat - minLat;
          const lngRange = maxLng - minLng;

          // Calcular número de pasos basado en el tamaño del área y resolución
          let maxLatSteps, maxLngSteps;
          if (resolution === 0) {
            maxLatSteps = Math.min(30, Math.ceil(latRange / stepSize));
            maxLngSteps = Math.min(30, Math.ceil(lngRange / stepSize));
          } else if (resolution === 1) {
            maxLatSteps = Math.min(80, Math.ceil(latRange / stepSize)); // Aumentado de 60 a 80 para res 1
            maxLngSteps = Math.min(80, Math.ceil(lngRange / stepSize));
          } else if (resolution <= 2) {
            maxLatSteps = Math.min(50, Math.ceil(latRange / stepSize));
            maxLngSteps = Math.min(50, Math.ceil(lngRange / stepSize));
          } else if (resolution <= 3) {
            maxLatSteps = Math.min(100, Math.ceil(latRange / stepSize)); // Aumentado para res 3
            maxLngSteps = Math.min(100, Math.ceil(lngRange / stepSize));
          } else if (resolution <= 5) {
            maxLatSteps = Math.min(80, Math.ceil(latRange / stepSize));
            maxLngSteps = Math.min(80, Math.ceil(lngRange / stepSize));
          } else if (resolution <= 8) {
            maxLatSteps = Math.min(120, Math.ceil(latRange / stepSize));
            maxLngSteps = Math.min(120, Math.ceil(lngRange / stepSize));
          } else if (resolution <= 11) {
            maxLatSteps = Math.min(150, Math.ceil(latRange / stepSize));
            maxLngSteps = Math.min(150, Math.ceil(lngRange / stepSize));
          } else {
            maxLatSteps = Math.min(200, Math.ceil(latRange / stepSize));
            maxLngSteps = Math.min(200, Math.ceil(lngRange / stepSize));
          }

          const adjustedLatStep = latRange / maxLatSteps;
          const adjustedLngStep = lngRange / maxLngSteps;

          // Muestrear puntos en todo el área bounds con pasos ajustados
          for (let i = 0; i <= maxLatSteps; i++) {
            for (let j = 0; j <= maxLngSteps; j++) {
              const lat = minLat + i * adjustedLatStep;
              const lng = minLng + j * adjustedLngStep;

              try {
                const cell = latLngToCell(lat, lng, resolution);
                if (!cells.has(cell)) {
                  // Verificar si la celda realmente intersecta con el bounds
                  const boundary = cellToBoundary(cell);
                  const cellBounds = new google.maps.LatLngBounds();

                  boundary.forEach((coord: [number, number]) => {
                    cellBounds.extend(
                      new google.maps.LatLng(coord[0], coord[1]),
                    );
                  });

                  const mapBounds = new google.maps.LatLngBounds(
                    new google.maps.LatLng(minLat, minLng),
                    new google.maps.LatLng(maxLat, maxLng),
                  );

                  if (mapBounds.intersects(cellBounds)) {
                    cells.add(cell);
                  }
                }
              } catch (error) {
                // Ignorar errores de coordenadas inválidas
              }
            }
          }

          return Array.from(cells);
        };

        // Obtener todas las celdas en el área visible
        const allCells = getCellsInBounds(
          sw.lat(),
          sw.lng(),
          ne.lat(),
          ne.lng(),
          resolution,
        );

        // Procesar todas las celdas encontradas
        allCells.forEach((cell) => {
          if (processedCells.has(cell)) return;

          try {
            const boundary = cellToBoundary(cell);
            const path = boundary.map((coord: [number, number]) => ({
              lat: coord[0],
              lng: coord[1],
            }));

            // Verificación final de bounds
            const cellBounds = new google.maps.LatLngBounds();
            path.forEach((point) => {
              cellBounds.extend(new google.maps.LatLng(point.lat, point.lng));
            });

            if (bounds.intersects(cellBounds)) {
              grids.push({
                id: cell,
                path,
                resolution,
                center: {
                  lat:
                    boundary.reduce((sum, coord) => sum + coord[0], 0) /
                    boundary.length,
                  lng:
                    boundary.reduce((sum, coord) => sum + coord[1], 0) /
                    boundary.length,
                },
              });
              processedCells.add(cell);
            }
          } catch (error) {
            console.warn("Error procesando celda H3:", error);
          }
        });

        // Actualizar grids
        setH3Grids(grids);
      } catch (error) {
        console.error("Error generando grid H3:", error);
        setH3Grids([]);
      } finally {
        setIsGeneratingGrids(false);
      }
    },
    [getH3Resolution, isGeneratingGrids, h3Grids],
  );

  // Referencia para evitar actualizaciones de zoom innecesarias
  const lastZoomRef = useRef<number>(DEFAULT_ZOOM);

  const handleMapEvents = useCallback(() => {
    if (!mapRef.current) return;

    const map = mapRef.current;
    const zoom = map.getZoom() || DEFAULT_ZOOM;

    // Solo actualizar currentZoom si realmente cambió (evita re-renders innecesarios)
    if (lastZoomRef.current !== zoom) {
      lastZoomRef.current = zoom;
      setCurrentZoom(zoom);
    }

    // Nota: mapBounds ya no se usa - el filtrado por bounds se eliminó para evitar parpadeo
    // H3 grids obtiene los bounds directamente del mapa en generateH3Grids

    if (generationTimeoutRef.current) {
      clearTimeout(generationTimeoutRef.current);
    }

    generationTimeoutRef.current = setTimeout(() => {
      generationTimeoutRef.current = null;
      if (showGrid) {
        generateH3Grids(map);
      } else if (h3Grids.length > 0) {
        setH3Grids([]);
      }
    }, 150);
  }, [generateH3Grids, showGrid, h3Grids.length]);

  const onLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      setMapInstance(map);
      // Sólo en desarrollo: el mapa a la mano para medir la cámara desde la
      // consola o una prueba (centro por cuadro, rebotes).
      if (import.meta.env.DEV) (window as unknown as { __dumaxMap?: google.maps.Map }).__dumaxMap = map;

      // Solo usar 'idle' - se dispara después de cualquier interacción (zoom, pan, resize)
      // Eliminamos 'dragend' y 'zoom_changed' porque 'idle' ya los cubre y evitamos llamadas duplicadas
      const idleListener = map.addListener("idle", () => {
        handleMapEvents();
      });

      const dragStartListener = map.addListener("dragstart", () => {
        const store = useVehiclesSocketStore.getState();
        if (store.focusedVehicleId) {
          store.setFocusedVehicleId(null);
        }
      });

      // El zoom NO deja de seguir la unidad (antes sí, y el "Siguiendo" se
      // quitaba al alejar): como en Google Maps o Uber, sólo arrastrar el mapa
      // significa "ya no la sigas".
      // Y mientras se sigue, la rueda acerca sobre la UNIDAD (centro), no
      // hacia el cursor: Google acerca hacia el cursor, la unidad quedaba
      // fuera del centro y el siguiente reporte la "regresaba" de un brinco.
      // (Un setCenter en zoom_changed no sirve: Google aplica su centro
      // después.) Los trackpads mandan muchos deltas chicos: se acumulan a
      // ~150 px por nivel.
      let wheelAcc = 0;
      const onWheel = (e: WheelEvent) => {
        if (!useVehiclesSocketStore.getState().focusedVehicleId) return;
        e.preventDefault();
        e.stopPropagation();
        wheelAcc += e.deltaY;
        // 150: una muesca de rueda (≈100-120) junto con el resto del giro da un
        // nivel; un deslizamiento de trackpad ya no encadena varios.
        if (Math.abs(wheelAcc) < 150) return;
        zoomTo(map, zoomTarget(map) + (wheelAcc < 0 ? 1 : -1));
        wheelAcc = 0;
      };
      map.getDiv().addEventListener("wheel", onWheel, { capture: true, passive: false });
      // Cualquier zoom (doble clic, pellizco, el escalonado) pausa la cámara
      // de seguimiento mientras Google lo anima, para no cortarlo.
      const zoomBusyListener = map.addListener("zoom_changed", () => {
        setMarkerZoom(Math.round(map.getZoom() ?? DEFAULT_ZOOM));
        const st = zoomSteps.get(map) ?? { target: Math.round(map.getZoom() ?? DEFAULT_ZOOM), timer: null, busyUntil: 0 };
        st.busyUntil = Date.now() + ZOOM_ANIM_MS;
        zoomSteps.set(map, st);
      });
      const zoomListener = {
        remove: () => map.getDiv().removeEventListener("wheel", onWheel, { capture: true }),
      };

      // Guardar referencias para limpiar
      (map as any)._h3Listeners = [
        idleListener,
        dragStartListener,
        zoomListener,
        zoomBusyListener,
      ];

      setTimeout(() => {
        window.google.maps.event.trigger(map, "resize");
        if (showGrid) {
          generateH3Grids(map, true);
        }
      }, 100);
    },
    [handleMapEvents, generateH3Grids, showGrid],
  );

  const onUnmount = useCallback(() => {
    // Limpiar listeners
    if (mapRef.current && (mapRef.current as any)._h3Listeners) {
      (mapRef.current as any)._h3Listeners.forEach(
        // .remove() sirve igual para los de Google y para el de la rueda.
        (listener: { remove: () => void }) => listener.remove(),
      );
    }

    if (generationTimeoutRef.current) {
      clearTimeout(generationTimeoutRef.current);
    }

    mapRef.current = null;
    setMapInstance(null);
  }, []);

  // Limpiar timeout al desmontar
  useEffect(() => {
    return () => {
      if (generationTimeoutRef.current) {
        clearTimeout(generationTimeoutRef.current);
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current) return;
    if (showGrid) {
      generateH3Grids(mapRef.current, true);
    } else if (h3Grids.length > 0) {
      setH3Grids([]);
    }
  }, [showGrid]);

  const fakeVehicles = useMemo(() => {
    if (!ENABLE_STATIC_VEHICLES) return [];

    return Object.values(staticVehicles.vehicles ?? {}).map((vehicle: any) => ({
      ...vehicle,
      status:
        vehicle.status === "moving" ? "driving" : (vehicle.status ?? "offline"),
      latitude: Number(vehicle.latitude) || 0,
      longitude: Number(vehicle.longitude) || 0,
      coordinates:
        vehicle.coordinates ?? `${vehicle.latitude},${vehicle.longitude}`,
      battery: vehicle.battery != null ? Number(vehicle.battery) : null,
      signal: Number(vehicle.signal) || 0,
      fuel: vehicle.fuel != null ? Number(vehicle.fuel) : null,
      temperature: Number(vehicle.temperature) || 0,
      odometer: vehicle.odometer != null ? Number(vehicle.odometer) : null,
      engineHours: Number(vehicle.engineHours) || 0,
      speed: vehicle.speed != null ? Number(vehicle.speed) : null,
      lastReport: vehicle.lastReport || {
        _seconds: Math.floor(Date.now() / 1000),
        _nanoseconds: 0,
      },
      driver: {
        name: vehicle.driver?.name ?? "Sin nombre",
        license: vehicle.driver?.license ?? "",
        photo: vehicle.driver?.photo ?? "",
        experience: vehicle.driver?.experience ?? "",
        phone: vehicle.driver?.phone ?? "",
      },
      H3: {
        res0: vehicle.H3?.res0 ?? "",
        res1: vehicle.H3?.res1 ?? "",
        res2: vehicle.H3?.res2 ?? "",
        res3: vehicle.H3?.res3 ?? "",
        res4: vehicle.H3?.res4 ?? "",
        res5: vehicle.H3?.res5 ?? "",
        res6: vehicle.H3?.res6 ?? "",
        res7: vehicle.H3?.res7 ?? "",
        res8: vehicle.H3?.res8 ?? "",
        res9: vehicle.H3?.res9 ?? "",
        res10: vehicle.H3?.res10 ?? "",
        res11: vehicle.H3?.res11 ?? "",
        res12: vehicle.H3?.res12 ?? "",
        res13: vehicle.H3?.res13 ?? "",
        res14: vehicle.H3?.res14 ?? "",
        res15: vehicle.H3?.res15 ?? "",
      },
      vehicle: {
        brand: vehicle.vehicle?.brand ?? "",
        model: vehicle.vehicle?.model ?? "",
        year: vehicle.vehicle?.year ?? 0,
        plates: vehicle.vehicle?.plates ?? "",
        color: vehicle.vehicle?.color ?? "",
        name: vehicle.vehicle?.name ?? "",
      },
    }));
  }, []);

  const mergedVehicles = useMemo(
    () => [...vehicles, ...fakeVehicles],
    [vehicles, fakeVehicles],
  );

  // Función para determinar el color de la cuadrícula basado en la resolución
  const getGridColor = (resolution: number) => {
    const colors = isDarkMode
      ? {
        veryLow: brand.primaryHover, // Verde para resoluciones muy bajas (0-2)
        low: "#22c55e", // Verde más oscuro para bajas (3-5)
        medium: "#60a5fa", // Azul para medias (6-8)
        high: "#f87171", // Rojo para altas (9-11)
        veryHigh: "#ef4444", // Rojo más oscuro para muy altas (12-14)
        ultraHigh: "#dc2626", // Rojo intenso para ultra altas (15+)
      }
      : {
        veryLow: "#16a34a",
        low: "#15803d",
        medium: "#2563eb",
        high: "#dc2626",
        veryHigh: "#b91c1c",
        ultraHigh: "#991b1b",
      };

    if (resolution <= 2) return colors.veryLow;
    if (resolution <= 5) return colors.low;
    if (resolution <= 8) return colors.medium;
    if (resolution <= 11) return colors.high;
    if (resolution <= 14) return colors.veryHigh;
    return colors.ultraHigh;
  };

  const themeClasses = {
    modalBg: isDarkMode ? "bg-[#3A3A3A]" : "bg-white",
    modalBorder: isDarkMode ? "border-gray-700" : "border-gray-200",
    bg: isDarkMode ? "bg-dark-card" : "bg-white",
    border: isDarkMode ? "border-dark-border" : "border-gray-200",
    text: isDarkMode ? "text-white" : "text-gray-900",
    textSecondary: isDarkMode ? "text-gray-400" : "text-gray-600",
    cardBg: isDarkMode ? "bg-dark-card" : "bg-white",
    cardBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    buttonHover: isDarkMode ? "hover:bg-dark-card" : "hover:bg-gray-100",
    buttonText: isDarkMode ? "text-gray-400" : "text-gray-600",
    buttonTextHover: isDarkMode ? "hover:text-white" : "hover:text-gray-900",
    inputBg: isDarkMode ? "bg-[#3A3A3A]" : "bg-gray-50",
    inputBorder: isDarkMode ? "border-dark-border" : "border-gray-200",
    inputText: isDarkMode ? "text-white" : "text-gray-900",
    inputPlaceholder: isDarkMode
      ? "placeholder-gray-500"
      : "placeholder-gray-500",
  };

  // Actualizar datos del vehículo seleccionado cuando cambien las posiciones
  // NOTA: No volver a solicitar address si ya lo tenemos para evitar flickering
  useEffect(() => {
    if (selectedUnit) {
      const vehicle = mergedVehicles.find(
        (vehicle) => vehicle.id === selectedUnit.id,
      );
      if (vehicle) {
        // Solo actualizar el estado, NO solicitar address de nuevo
        // El address ya fue solicitado al abrir el modal
        setSelectedUnit(vehicle);
      }
    }
  }, [mergedVehicles]);

  useEffect(() => {
    if (!selectedUnit || !focusedVehicleId) return;
    if (selectedUnit.id === focusedVehicleId) return;
    const vehicle = mergedVehicles.find((v) => v.id === focusedVehicleId);
    if (vehicle) {
      setSelectedUnit(vehicle);
      requestAddressForDevice(focusedVehicleId);
    }
  }, [focusedVehicleId, selectedUnit, mergedVehicles, requestAddressForDevice]);

  const followedVehicle = useMemo(
    () =>
      focusedVehicleId
        ? mergedVehicles.find((v) => v.id === focusedVehicleId)
        : undefined,
    [focusedVehicleId, mergedVehicles],
  );

  const focusZoomedIdRef = useRef<string | null>(null);
  const focusStartedAtRef = useRef(0);

  // Sólo el panel de datos en vivo (ancho, a la derecha) corre la cámara. La
  // ficha de la unidad es una ventana de 320 px en la esquina que no tapa el
  // centro: correr la cámara al abrirla/cerrarla era un brinco gratis.
  const hasInfoPanel = Boolean(liveDataDeviceId);
  const followOffsetRef = useRef(0);
  followOffsetRef.current =
    window.innerWidth >= COMPACT_VIEWPORT_MAX_WIDTH && hasInfoPanel ? FOCUS_PANEL_OFFSET_X : 0;
  // Último centro que puso el seguimiento (lo usa el zoom con rueda).
  const followCenterRef = useRef<Punto | null>(null);

  // La cámara SIGUE AL MARCADOR cuadro a cuadro (CustomMarker.onDrawn), en
  // vez de tener su propia animación: dos animaciones con relojes distintos
  // hacían vibrar el marcador respecto al mapa y, con tramos largos, la vista
  // se adelantaba. Durante el paneo inicial no se pisa (un setCenter lo corta).
  const catchUpRef = useRef<number | null>(null);
  const onFollowedDrawn = useCallback((p: Punto) => {
    const map = mapRef.current;
    if (!map) return;
    // En pausa (paneo inicial o un paso de zoom) no se mueve la cámara, pero
    // al terminar hay que ALCANZAR al marcador: si su tramo terminó durante la
    // pausa ya no habría más cuadros que la recentraran hasta el siguiente
    // reporte (30-60 s) y la unidad quedaba fuera del centro.
    const now = Date.now();
    const wait = Math.max(focusStartedAtRef.current + 600 - now, (zoomSteps.get(map)?.busyUntil ?? 0) - now);
    if (wait > 0) {
      if (catchUpRef.current == null) {
        catchUpRef.current = window.setTimeout(() => {
          catchUpRef.current = null;
          const id = useVehiclesSocketStore.getState().focusedVehicleId;
          const last = id ? DRAWN_POSITIONS.get(id) : undefined;
          if (last) onFollowedDrawnRef.current(last);
        }, wait + 30);
      }
      return;
    }
    const c = getShiftedCenter(map, p.lat, p.lng, followOffsetRef.current);
    const prev = followCenterRef.current;
    followCenterRef.current = c;
    // Un tramo largo no se desliza: el marcador salta (TRAMO_MAX_M) y llega
    // un solo cuadro. Con moveCamera la vista daba un corte seco de ~1.7 km
    // cada minuto; panTo lo convierte en un paneo de Google.
    if (prev && metrosEntre(prev, c) > TRAMO_MAX_M) {
      map.panTo(c);
      return;
    }
    // moveCamera es la API de Google para mover la cámara dentro de un loop
    // de animación; setCenter cada cuadro se aplicaba con retraso y el
    // marcador se adelantaba 25-50 px al centro antes de volver (medido).
    map.moveCamera({ center: c });
  }, []);
  const onFollowedDrawnRef = useRef(onFollowedDrawn);
  onFollowedDrawnRef.current = onFollowedDrawn;

  const lastOffsetRef = useRef(0);
  useEffect(() => {
    if (!focusedVehicleId) {
      focusZoomedIdRef.current = null;
      followCenterRef.current = null;
      return;
    }
    const map = mapRef.current;
    if (!map || !followedVehicle?.latitude || !followedVehicle?.longitude) return;
    const offsetChanged = lastOffsetRef.current !== followOffsetRef.current;
    lastOffsetRef.current = followOffsetRef.current;
    // Donde SE VE la unidad, no su último reporte (el dibujo va detrás).
    const seen = DRAWN_POSITIONS.get(focusedVehicleId) ?? { lat: followedVehicle.latitude, lng: followedVehicle.longitude };
    const target = getShiftedCenter(map, seen.lat, seen.lng, followOffsetRef.current);

    if (focusZoomedIdRef.current !== focusedVehicleId) {
      focusZoomedIdRef.current = focusedVehicleId;
      focusStartedAtRef.current = Date.now();
      // Primero centrar (panTo anima) y luego volar el zoom: un setZoom a
      // media animación de panTo la corta.
      followCenterRef.current = target;
      map.panTo(target);
      if ((map.getZoom() ?? 0) < FOCUS_ZOOM) window.setTimeout(() => zoomTo(map, FOCUS_ZOOM), 350);
      return;
    }
    // Sin marcadores dibujados (íconos ocultos) nadie mueve la cámara; y si
    // se abre/cierra el panel ancho, re-encuadrar con su desplazamiento.
    if (!showIcons || offsetChanged) {
      followCenterRef.current = target;
      map.panTo(target);
    }
  }, [
    focusedVehicleId,
    followedVehicle?.latitude,
    followedVehicle?.longitude,
    hasInfoPanel,
    showIcons,
  ]);

  // Referencia para mantener vehículos válidos estables
  const prevValidVehiclesRef = useRef<any[]>([]);

  // Validar vehículos sin filtrar por bounds - Google Maps maneja la visibilidad automáticamente
  // Filtrar por bounds causaba parpadeo al mover el mapa porque mapBounds cambia constantemente
  const validVehicles = useMemo(() => {
    const result = mergedVehicles.filter(hasMapPosition);

    // Comparar con el resultado anterior para evitar re-renders
    const prev = prevValidVehiclesRef.current;
    if (prev.length === result.length) {
      let same = true;
      for (let i = 0; i < result.length; i++) {
        if (
          prev[i].id !== result[i].id ||
          prev[i].latitude !== result[i].latitude ||
          prev[i].longitude !== result[i].longitude ||
          prev[i].status !== result[i].status ||
          // El modelo elegido en el garaje: sin esto el mapa reusaba el
          // arreglo viejo y la unidad cambiaba de puntero hasta su siguiente
          // movimiento (una detenida, minutos después).
          prev[i].vehicle?.pointerModel !== result[i].vehicle?.pointerModel
        ) {
          same = false;
          break;
        }
      }
      if (same) return prev;
    }

    prevValidVehiclesRef.current = result;
    return result;
  }, [mergedVehicles]);

  // Al elegir compañía el mapa se quedaba en el centro de México a zoom 5: con
  // la flota de ACUAPUE en Puebla se veía un solo grupo «26» sobre toda
  // Norteamérica. Se encuadra la flota UNA vez por compañía, cuando llegan sus
  // unidades; los reportes siguientes no mueven la cámara, y si ya hay una
  // unidad seguida, manda ella.
  const framedCompanyRef = useRef<string | null>(null);
  // Cada cambio de compañía vuelve a encuadrar, también al regresar a una ya
  // vista (A → B sin unidades → A). Va antes del efecto que encuadra.
  useEffect(() => {
    framedCompanyRef.current = null;
  }, [selectedCompany]);
  useEffect(() => {
    const map = mapInstance;
    if (!map || !selectedCompany || framedCompanyRef.current === selectedCompany) return;
    const pts = validVehicles.filter((v) => v.companyId === selectedCompany);
    if (!pts.length) return;
    framedCompanyRef.current = selectedCompany;
    if (useVehiclesSocketStore.getState().focusedVehicleId) return;
    if (pts.length === 1) {
      map.panTo({ lat: Number(pts[0].latitude), lng: Number(pts[0].longitude) });
      map.setZoom(15);
      return;
    }
    const bounds = new google.maps.LatLngBounds();
    pts.forEach((v) => bounds.extend({ lat: Number(v.latitude), lng: Number(v.longitude) }));
    map.fitBounds(bounds, 80);
    // Unidades casi encimadas darían un zoom de banqueta (como handleClusterClick).
    google.maps.event.addListenerOnce(map, "idle", () => {
      if ((map.getZoom() ?? 0) > 16) map.setZoom(16);
    });
  }, [mapInstance, selectedCompany, validVehicles]);

  // Referencia para mantener grupos estables
  const prevGroupedVehiclesRef = useRef<any[]>([]);

  // Agrupar vehículos por celda H3
  const groupedVehicles = useMemo(() => {
    const zoom = currentZoom || DEFAULT_ZOOM;

    // A partir de zoom 10, no agrupar vehículos - mostrar individualmente
    if (zoom >= 10) {
      const individualResult = validVehicles.map((vehicle) => ({
        id: `individual_${vehicle.id}`,
        vehicles: [vehicle],
        center: { lat: vehicle.latitude, lng: vehicle.longitude },
        count: 1,
        isCluster: false,
      }));

      // Comparar con resultado anterior
      const prev = prevGroupedVehiclesRef.current;
      if (prev.length === individualResult.length) {
        let same = true;
        for (let i = 0; i < individualResult.length; i++) {
          const p = prev[i];
          const n = individualResult[i];
          if (
            p.id !== n.id ||
            p.center.lat !== n.center.lat ||
            p.center.lng !== n.center.lng ||
            p.vehicles[0]?.status !== n.vehicles[0]?.status ||
            p.vehicles[0]?.vehicle?.pointerModel !== n.vehicles[0]?.vehicle?.pointerModel
          ) {
            same = false;
            break;
          }
        }
        if (same) return prev;
      }

      prevGroupedVehiclesRef.current = individualResult;
      return individualResult;
    }

    // Para zoom 0-7, agrupar por celda H3
    const resolution = getH3Resolution(zoom);
    const groups = new Map<string, any[]>();
    const errorVehicles: any[] = [];

    // La unidad seguida nunca se agrupa: al alejar se perdía dentro de un
    // círculo con número mientras la cámara la seguía centrando.
    const followed = validVehicles.find((v) => v.id === focusedVehicleId);

    validVehicles.forEach((vehicle) => {
      if (vehicle === followed) return;
      try {
        if (
          !vehicle.latitude ||
          !vehicle.longitude ||
          isNaN(vehicle.latitude) ||
          isNaN(vehicle.longitude) ||
          (vehicle.latitude === 0 && vehicle.longitude === 0)
        ) {
          return;
        }

        const lat = Math.round(vehicle.latitude * 1000000) / 1000000;
        const lng = Math.round(vehicle.longitude * 1000000) / 1000000;
        const h3Cell = latLngToCell(lat, lng, resolution);

        if (!groups.has(h3Cell)) {
          groups.set(h3Cell, []);
        }
        groups.get(h3Cell)!.push(vehicle);
      } catch (error) {
        errorVehicles.push(vehicle);
      }
    });

    if (errorVehicles.length > 0) {
      errorVehicles.forEach((vehicle) => {
        let foundGroup = false;

        for (const [_, vehicles] of groups.entries()) {
          const avgLat =
            vehicles.reduce((sum, v) => sum + v.latitude, 0) / vehicles.length;
          const avgLng =
            vehicles.reduce((sum, v) => sum + v.longitude, 0) / vehicles.length;

          const distance = Math.sqrt(
            Math.pow(vehicle.latitude - avgLat, 2) +
            Math.pow(vehicle.longitude - avgLng, 2),
          );

          if (distance < 0.001) {
            vehicles.push(vehicle);
            foundGroup = true;
            break;
          }
        }

        if (!foundGroup) {
          const uniqueKey = `error_${vehicle.id}`;
          groups.set(uniqueKey, [vehicle]);
        }
      });
    }

    const result = Array.from(groups.entries())
      .map(([h3Cell, vehicles]) => {
        if (!vehicles || vehicles.length === 0) {
          return null;
        }

        const centerLat =
          vehicles.reduce((sum, v) => sum + v.latitude, 0) / vehicles.length;
        const centerLng =
          vehicles.reduce((sum, v) => sum + v.longitude, 0) / vehicles.length;

        return {
          id: h3Cell,
          vehicles,
          center: { lat: centerLat, lng: centerLng },
          count: vehicles.length,
          isCluster: vehicles.length > 1,
        };
      })
      .filter((group) => group !== null);
    if (followed) {
      result.push({
        id: `individual_${followed.id}`,
        vehicles: [followed],
        center: { lat: followed.latitude, lng: followed.longitude },
        count: 1,
        isCluster: false,
      });
    }

    // Comparar con resultado anterior para evitar re-renders
    const prev = prevGroupedVehiclesRef.current;
    if (prev.length === result.length) {
      let same = true;
      for (let i = 0; i < result.length; i++) {
        const p = prev[i];
        const n = result[i];
        if (
          p.id !== n.id ||
          p.count !== n.count ||
          Math.abs(p.center.lat - n.center.lat) > 0.00001 ||
          Math.abs(p.center.lng - n.center.lng) > 0.00001 ||
          p.vehicles[0]?.vehicle?.pointerModel !== n.vehicles[0]?.vehicle?.pointerModel
        ) {
          same = false;
          break;
        }
      }
      if (same) return prev;
    }

    prevGroupedVehiclesRef.current = result;
    return result;
  }, [validVehicles, currentZoom, getH3Resolution, focusedVehicleId]);

  // Etiquetas que caben sin encimarse (labelCollision.ts): con 40 unidades a
  // ~1 km a zoom de ciudad salía una columna de etiquetas montadas donde no se
  // leía ninguna. Primero la unidad seguida o abierta, después las que se mueven.
  const unitDisplay = useUnitDisplay();
  const labeledIds = useMemo(() => {
    if (!showVehicleLabels) return null;
    const candidates = groupedVehicles
      .filter((g) => !g.isCluster)
      .map((g) => {
        const v = g.vehicles[0];
        const speed = liveSpeedKmh(v.speed, v.status);
        const pinned = v.id === focusedVehicleId || v.id === selectedUnit?.id;
        return {
          id: v.id,
          lat: Number(v.latitude),
          lng: Number(v.longitude),
          name: v.vehicle?.name || v.fullId || "",
          speed: rowSpeed(speed),
          priority: pinned ? 2 : speed ? 1 : 0,
        };
      });
    return visibleLabelIds(candidates, currentZoom || DEFAULT_ZOOM, unitDisplay);
  }, [showVehicleLabels, groupedVehicles, currentZoom, unitDisplay, focusedVehicleId, selectedUnit?.id]);

  // Tocar un grupo encuadra sus unidades. Ya no abre la tarjeta «Clúster»
  // con su modal: nadie la usaba y tapaba el mapa (Daniel, 30-oct).
  const handleClusterClick = useCallback((cluster: any) => {
    if (!mapRef.current || !cluster) return;

    const map = mapRef.current;
    // Soltar la unidad seguida: su cámara re-centra cada cuadro y deshacía el
    // encuadre del grupo.
    useVehiclesSocketStore.getState().setFocusedVehicleId(null);

    // Encuadrar TODAS las unidades del grupo. Antes iba a su centro a zoom
    // 11: con unidades separadas ese centro caía donde no había ninguna.
    const pts = (cluster.vehicles || []).filter((v: any) => Number.isFinite(Number(v.latitude)) && Number.isFinite(Number(v.longitude)));
    if (pts.length > 1) {
      const bounds = new google.maps.LatLngBounds();
      pts.forEach((v: any) => bounds.extend({ lat: Number(v.latitude), lng: Number(v.longitude) }));
      map.fitBounds(bounds, 80);
      // Unidades casi encimadas darían un zoom de banqueta.
      google.maps.event.addListenerOnce(map, "idle", () => {
        if ((map.getZoom() ?? 0) > 16) map.setZoom(16);
      });
    } else {
      map.panTo(cluster.center);
      map.setZoom(15);
    }

    // Clear any selected unit
    setSelectedUnit(null);
  }, []);

  if (!isLoaded) {
    return (
      <div className="h-full flex items-center justify-center">
        Cargando mapa...
      </div>
    );
  }

  if (loadError) {
    return (
      <div className="h-full flex items-center justify-center text-red-500">
        Error al cargar el mapa: {loadError.message}
      </div>
    );
  }

  return (
    <>
      <div
        className={`h-full ${themeClasses.bg} border ${themeClasses.border} overflow-hidden relative`}
      >
        <div className="absolute inset-0 isolate">
        <GoogleMap
          mapContainerStyle={{ width: "100%", height: "100%" }}
          center={DEFAULT_CENTER}
          zoom={DEFAULT_ZOOM}
          onLoad={onLoad}
          onUnmount={onUnmount}
          onClick={handleMapClick}
          options={{
            minZoom: 4,
            // Tope de acercamiento: en 20 un tracto mide ~52 px a escala real;
            // más cerca se agrandaría de más y cada reporte movería mucho el mapa.
            maxZoom: 20,
            mapTypeId: mapType as google.maps.MapTypeId,
            mapTypeControl: false,
            // Los controles nativos de Google (pantalla completa, flechas de cámara,
            // muñequito de Street View) son cuadros blancos que no se pueden
            // restilizar y no combinaban con el resto (Daniel, 30-oct). Pantalla
            // completa y zoom van como botones propios (mapChrome); Street View
            // queda fuera mientras Daniel decide si se usa.
            fullscreenControl: false,
            cameraControl: false,
            streetViewControl: false,
            zoomControl: false,
            styles: mapBaseStyles(isDarkMode, mapType),
          }}
        >
          {showGrid &&
            h3Grids
              .filter(
                (grid) =>
                  grid.resolution ===
                  getH3Resolution(currentZoom || DEFAULT_ZOOM),
              )
              .map((grid) => {
                const gridColor = getGridColor(grid.resolution);
                const fillOpacity = isDarkMode
                  ? grid.resolution <= 2
                    ? 0.02
                    : grid.resolution <= 5
                      ? 0.03
                      : grid.resolution <= 8
                        ? 0.05
                        : grid.resolution <= 11
                          ? 0.07
                          : grid.resolution <= 14
                            ? 0.09
                            : 0.11
                  : grid.resolution <= 2
                    ? 0.01
                    : grid.resolution <= 5
                      ? 0.02
                      : grid.resolution <= 8
                        ? 0.03
                        : grid.resolution <= 11
                          ? 0.04
                          : grid.resolution <= 14
                            ? 0.05
                            : 0.06;

                return (
                  <Polygon
                    key={grid.id}
                    paths={grid.path}
                    options={{
                      strokeColor: gridColor,
                      strokeOpacity: 0.7,
                      strokeWeight:
                        grid.resolution <= 5
                          ? 1
                          : grid.resolution <= 10
                            ? 1.5
                            : grid.resolution <= 14
                              ? 2
                              : 2.5,
                      fillColor: gridColor,
                      fillOpacity: fillOpacity,
                      clickable: false,
                      zIndex: 1,
                    }}
                  />
                );
              })}

          {/* Render Geofences */}
          {showGeofences &&
            geofences.map((geofence) => {
              if (
                geofence.shape === "circular" &&
                (geofenceFilter === "all" || geofenceFilter === "circular") &&
                !Array.isArray(geofence.coordinates)
              ) {
                // Render circular geofence
                const circleCoords = geofence.coordinates as {
                  latitude: number;
                  longitude: number;
                  radius: number;
                };

                // Generate circle points
                const circlePoints: { lat: number; lng: number }[] = [];
                const numPoints = 64; // Number of points to approximate circle
                const radiusInMeters = circleCoords.radius;

                for (let i = 0; i <= numPoints; i++) {
                  const angle = (i / numPoints) * 2 * Math.PI;
                  const lat =
                    circleCoords.latitude +
                    (radiusInMeters / 111320) * Math.cos(angle);
                  const lng =
                    circleCoords.longitude +
                    (radiusInMeters /
                      (111320 *
                        Math.cos((circleCoords.latitude * Math.PI) / 180))) *
                    Math.sin(angle);
                  circlePoints.push({ lat, lng });
                }

                return (
                  <Polygon
                    key={geofence.id}
                    paths={circlePoints}
                    options={{
                      strokeColor: geofence.color || "#2563eb",
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      fillColor: geofence.color || "#2563eb",
                      fillOpacity: isGeofenceActive(geofence.status_id)
                        ? 0.2
                        : 0.1,
                      clickable: true,
                      zIndex: 2,
                    }}
                  />
                );
              } else if (
                geofence.shape === "polygonal" &&
                (geofenceFilter === "all" || geofenceFilter === "polygonal") &&
                Array.isArray(geofence.coordinates)
              ) {
                // Render polygonal geofence
                const polygonCoords = geofence.coordinates.map((coord) => ({
                  lat: coord.latitude,
                  lng: coord.longitude,
                }));

                return (
                  <Polygon
                    key={geofence.id}
                    paths={polygonCoords}
                    options={{
                      strokeColor: geofence.color || "#2563eb",
                      strokeOpacity: 0.8,
                      strokeWeight: 2,
                      fillColor: geofence.color || "#2563eb",
                      fillOpacity: isGeofenceActive(geofence.status_id)
                        ? 0.2
                        : 0.1,
                      clickable: true,
                      zIndex: 2,
                    }}
                  />
                );
              } else if (
                geofence.shape === "linear" &&
                (geofenceFilter === "all" || geofenceFilter === "linear") &&
                Array.isArray(geofence.coordinates)
              ) {
                const axis = resolveLinearAxis(
                  geofence.coordinates_dense,
                  geofence.coordinates
                );
                if (axis.length < 2) return null;

                const corridorColor = geofence.color || "#2563eb";
                const corridorRing = buildCorridorRing(
                  axis,
                  geofence.buffer ?? LINEAR_BUFFER_DEFAULT
                );

                return (
                  <Fragment key={geofence.id}>
                    {corridorRing.length > 0 && (
                      <Polygon
                        paths={corridorRing}
                        options={{
                          strokeColor: corridorColor,
                          strokeOpacity: 0.7,
                          strokeWeight: 1,
                          fillColor: corridorColor,
                          fillOpacity: isGeofenceActive(geofence.status_id)
                            ? 0.2
                            : 0.1,
                          clickable: true,
                          zIndex: 2,
                        }}
                      />
                    )}
                    <GeofenceAxisPolyline
                      color={corridorColor}
                      path={axis}
                      map={mapInstance}
                    />
                  </Fragment>
                );
              }
              return null;
            })}

          {/* Routes are rendered outside <GoogleMap> via RouteDirectionsPolyline
              so the path follows real roads (same as the route form preview). */}

          {showTraffic && <TrafficLayer />}
          {showTransit && <TransitLayer />}
          {showBicycling && <BicyclingLayer />}

          {showRuler && rulerPoints.length > 0 && (
            <>
              {/* Polyline is managed by useEffect for proper cleanup */}

              {/* Render draggable markers for ruler points */}
              {rulerPoints.map((point, index) => (
                <Marker
                  key={`ruler-point-${index}-${point.lat}-${point.lng}`}
                  position={point}
                  draggable={true}
                  onDrag={(e) => {
                    if (e.latLng) {
                      const newPoint = {
                        lat: e.latLng.lat(),
                        lng: e.latLng.lng(),
                      };
                      setRulerPoints((prev) => {
                        const updated = [...prev];
                        updated[index] = newPoint;
                        return updated;
                      });
                    }
                  }}
                  icon={{
                    path: google.maps.SymbolPath.CIRCLE,
                    scale: 8,
                    fillColor: brand.primary,
                    fillOpacity: 1,
                    strokeColor: "#ffffff",
                    strokeWeight: 2,
                  }}
                  label={{
                    text: (index + 1).toString(),
                    color: "#ffffff",
                    fontSize: "12px",
                    fontWeight: "bold",
                  }}
                />
              ))}

              {/* Display distance label for multi-point paths */}
              {rulerPoints.length >= 2 && (
                <OverlayView
                  position={{
                    lat: rulerPoints[Math.floor(rulerPoints.length / 2)].lat,
                    lng: rulerPoints[Math.floor(rulerPoints.length / 2)].lng,
                  }}
                  mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
                  getPixelPositionOffset={(width, height) => ({
                    x: -(width / 2),
                    y: -(height / 2) - 20,
                  })}
                >
                  <div
                    className={`px-3 py-2 min-w-[120px] rounded-lg shadow-lg border-2 border-primary-500 font-semibold whitespace-nowrap ${isDarkMode ? "bg-gray-800 text-white" : "bg-white text-gray-900"}`}
                    style={{
                      transform: "translateZ(0)",
                      willChange: "auto",
                      backfaceVisibility: "hidden",
                    }}
                  >
                    <div className="flex items-center space-x-2">
                      <i className="ri-ruler-line text-primary-500"></i>
                      <span>
                        {calculateTotalDistance(rulerPoints).toFixed(2)} km
                      </span>
                    </div>
                    <div className="text-xs text-gray-500 mt-1 text-center">
                      {(calculateTotalDistance(rulerPoints) * 1000).toFixed(0)}{" "}
                      m
                    </div>
                  </div>
                </OverlayView>
              )}
            </>
          )}
        </GoogleMap>
        </div>

        <MapLoadingOverlay
          ready={mapReady}
          status={mapReadyStatus}
          isDarkMode={isDarkMode}
        />

        {(geofencesQuery.isFetching ||
          poiQuery.isFetching ||
          routesQuery.isFetching) && (
          <div className="absolute top-3 left-1/2 -translate-x-1/2 z-30 flex items-center gap-2 px-3 py-1.5 rounded-full shadow-lg bg-gray-900/90 text-white text-xs">
            <span className="w-3 h-3 border-2 border-white/40 border-t-white rounded-full animate-spin" />
            <span>
              {poiQuery.isFetching
                ? t("map.loadingPoints", "Cargando puntos...")
                : routesQuery.isFetching
                  ? t("map.loadingRoutes", "Cargando rutas...")
                  : t("map.loadingGeofences", "Cargando geocercas...")}
            </span>
          </div>
        )}

        {/* Pastilla de la unidad: "Siguiendo" + la ficha minimizada en un solo
            lugar. Tocarla abre la ficha; la ✕ deja de seguir y la cierra. */}
        {(() => {
          const pillUnit = followedVehicle ?? (selectedUnit && cardMinimized ? selectedUnit : null);
          if (!pillUnit) return null;
          const cardOpen = !!selectedUnit && !cardMinimized && selectedUnit.id === pillUnit.id;
          const pillSpeed = liveSpeedKmh(pillUnit.speed, pillUnit.status);
          // Mismo vidrio, alto y radio que las píldoras de capas (Daniel, 30-oct):
          // era el único globo redondo y gris oscuro del mapa.
          const chrome = mapChrome(isDarkMode);
          return (
            <>
            {/* Móvil: la pastilla se encimaba con los botones de filtro y búsqueda
                (Daniel, 29-oct). En su lugar, un botón que alterna mapa ↔ vista de manejo. */}
            {canDrawDriveView() && <button
              type="button"
              onClick={() => toggleDriveView(pillUnit.id)}
              aria-pressed={driveViewOpenId === pillUnit.id}
              aria-label={t("map.driveView", "Vista de manejo")}
              title={t("map.driveView", "Vista de manejo")}
              className={`md:hidden absolute top-[3.25rem] left-3 z-20 ${mapIconButton(isDarkMode)} ${
                driveViewOpenId === pillUnit.id ? (isDarkMode ? "!bg-gray-100 !text-gray-900" : "!bg-gray-900 !text-white") : ""
              }`}
            >
              <i className={`${driveViewOpenId === pillUnit.id ? "ri-map-2-line" : "ri-gamepad-line"} text-[17px]`} aria-hidden />
            </button>}
            <div data-unit-pill className={`absolute top-[3.25rem] left-3 z-20 hidden md:flex items-stretch h-8 rounded-[10px] text-[13px] motion-safe:animate-scaleIn origin-top-left ${mapChromeBase} ${chrome.surface}`}>
              <button
                type="button"
                onClick={() => {
                  if (cardOpen) {
                    window.dispatchEvent(new Event(MINIMIZE_EVENT));
                    return;
                  }
                  setSelectedUnit(pillUnit);
                  setCardMinimized(false);
                  setCardFromPill(true);
                  requestAddressForDevice(pillUnit.id);
                }}
                aria-expanded={cardOpen}
                title={cardOpen ? t("unitModal.minimize", "Minimizar") : t("unitModal.expand", "Abrir ficha")}
                className={`flex items-center gap-2 pl-2.5 pr-2 rounded-l-[9px] transition-colors duration-150 ${chrome.hover}`}
              >
                {followedVehicle ? (
                  <span className="relative flex h-2 w-2">
                    <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-primary-500 opacity-75"></span>
                    <span className="relative inline-flex rounded-full h-2 w-2 bg-primary-500"></span>
                  </span>
                ) : (
                  <span className="h-2 w-2 rounded-full bg-gray-400" />
                )}
                <span className="font-medium max-w-[220px] truncate">
                  {followedVehicle ? `${t("map.following", "Siguiendo")}: ` : ""}
                  {pillUnit.vehicle?.name || pillUnit.fullId || pillUnit.id}
                </span>
                {pillSpeed != null && pillSpeed >= 1 && (
                  <span className={`tabular-nums ${chrome.muted}`}>{Math.round(pillSpeed)} km/h</span>
                )}
                <i className={`${cardOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-base ${chrome.muted}`} aria-hidden />
              </button>
              <button
                type="button"
                aria-label={t("unitsTable.close", "Cerrar")}
                onClick={() => {
                  setFocusedVehicleId(null);
                  setSelectedUnit(null);
                  setCardMinimized(false);
                }}
                className={`px-2 rounded-r-[9px] flex items-center justify-center border-l transition-colors duration-150 ${chrome.line} ${chrome.hover}`}
              >
                <i className="ri-close-line text-sm"></i>
              </button>
            </div>
            </>
          );
        })()}

        {/* Render Vehicle Route Tracking Polylines - fuera del GoogleMap para usar API nativa */}
        {mapInstance &&
          activeTrackings.map((tracking) => (
            <RouteHistoryPolylines
              key={`tracking-${tracking.vehicleId}`}
              segments={tracking.segments}
              map={mapInstance}
            />
          ))}

        {/* Render Stop Markers */}
        {mapInstance &&
          activeTrackings.map((tracking) => (
            <StopMarkers
              key={`stop-markers-${tracking.vehicleId}`}
              stateMarkers={tracking.stateMarkers || []}
              map={mapInstance}
            />
          ))}

        {/* Render route polylines following real driving paths */}
        {mapInstance &&
          routePaths.map((rp) => (
            <RouteDirectionsPolyline
              key={`route-dir-${rp.id}`}
              routeId={rp.id}
              points={rp.points}
              axis={rp.axis}
              buffer={rp.buffer}
              firstPointRadius={rp.firstPointRadius}
              lastPointRadius={rp.lastPointRadius}
              color="#F59E0B"
              map={mapInstance}
            />
          ))}

        {/* Render Route Start Flags */}
        {mapInstance &&
          activeTrackings.map((tracking) => {
            if (!tracking.startPoint) return null;
            return (
              <StartFlagMarker
                key={`route-start-flag-${tracking.vehicleId}`}
                lat={tracking.startPoint.lat}
                lng={tracking.startPoint.lng}
                color={tracking.color}
                map={mapInstance}
                type="route"
              />
            );
          })}

        {/* Render Route End Flags (último punto reportado del recorrido) */}
        {mapInstance &&
          activeTrackings.map((tracking) => {
            if (!tracking.endPoint) return null;
            return (
              <EndFlagMarker
                key={`route-end-flag-${tracking.vehicleId}`}
                lat={tracking.endPoint.lat}
                lng={tracking.endPoint.lng}
                color={tracking.color}
                map={mapInstance}
                type="route"
              />
            );
          })}

        {/* Render Vehicle Footprint Tracking Polylines */}
        {mapInstance &&
          activeFootprints.map((footprint) => (
            <VehicleFootprintPolyline
              key={`footprint-${footprint.vehicleId}`}
              vehicleId={footprint.vehicleId}
              color={footprint.color}
              points={footprint.points}
              map={mapInstance}
            />
          ))}

        {/* Render Footprint Start Flags */}
        {mapInstance &&
          activeFootprints.map((footprint) => {
            if (!footprint.startPoint) return null;
            return (
              <StartFlagMarker
                key={`footprint-start-flag-${footprint.vehicleId}`}
                lat={footprint.startPoint.lat}
                lng={footprint.startPoint.lng}
                color={footprint.color}
                map={mapInstance}
                type="footprint"
              />
            );
          })}

        {/* Render Footprint End Flags (siguiendo al vehiculo) */}
        {mapInstance &&
          activeFootprints.map((footprint) => {
            const vehicle = vehiclesById.get(footprint.vehicleId);
            if (!vehicle || !vehicle.latitude || !vehicle.longitude)
              return null;
            return (
              <EndFlagMarker
                key={`footprint-end-flag-${footprint.vehicleId}`}
                lat={vehicle.latitude}
                lng={vehicle.longitude}
                color={footprint.color}
                map={mapInstance}
                type="footprint"
              />
            );
          })}

        {/* Render Vehicle Markers - fuera del GoogleMap para usar overlays nativos sin parpadeo */}
        {mapInstance &&
          showIcons &&
          groupedVehicles.map((group) => {
            if (group.isCluster) {
              return (
                <ClusteredMarker
                  key={group.id}
                  cluster={group}
                  map={mapInstance}
                  isFocused={false}
                  onClick={() => {
                    handleClusterClick(group);
                  }}
                />
              );
            } else {
              const vehicle = group.vehicles[0];
              return (
                <CustomMarker
                  key={vehicle.id}
                  vehicle={vehicle}
                  map={mapInstance}
                  showLabel={!!labeledIds?.has(vehicle.id)}
                  zoom={markerZoom}
                  onDrawn={focusedVehicleId === vehicle.id ? onFollowedDrawn : undefined}
                  isFocused={
                    focusedVehicleId === vehicle.id ||
                    selectedUnit?.id === vehicle.id
                  }
                  onClick={() => {
                    // Tocar el marcador de la ficha abierta Y seguida la
                    // minimiza (y la vuelve a abrir); cualquier otro la abre.
                    // Si se soltó (clic en el mapa), tocarla otra vez la vuelve
                    // a seguir y acerca: antes sólo alternaba la ficha y la
                    // cámara ya no iba a la unidad.
                    const followed = useVehiclesSocketStore.getState().focusedVehicleId === vehicle.id;
                    if (selectedUnitIdRef.current === vehicle.id && followed) {
                      setCardFromPill(true);
                      // Abierta: que la ficha haga su animación hacia la
                      // pastilla (ella misma avisa al terminar).
                      if (!cardMinimizedRef.current) window.dispatchEvent(new Event(MINIMIZE_EVENT));
                      else setCardMinimized(false);
                      return;
                    }
                    setCardMinimized(false);
                    setCardFromPill(false);
                    setSelectedUnit(vehicle);
                    setFocusedVehicleId(vehicle.id);
                    requestAddressForDevice(vehicle.id);
                  }}
                />
              );
            }
          })}

        {/* Render Interest Points - fuera del GoogleMap para usar overlays nativos sin parpadeo */}
        {mapInstance &&
          showInterestPoints &&
          filteredPointsOfInterest.map((point) => (
            <InterestPointMarker
              key={point.id}
              point={point}
              map={mapInstance}
              isSelected={selectedInterestPoint?.id === point.id}
              isDarkMode={isDarkMode}
              onClick={() => {
                setSelectedInterestPoint(point);
                // Cerrar otros modales
                setSelectedUnit(null);
              }}
            />
          ))}

        {/* Popup de información del punto de interés seleccionado */}
        {mapInstance && selectedInterestPoint && (
          <InterestPointInfoPopup
            point={selectedInterestPoint}
            map={mapInstance}
            isDarkMode={isDarkMode}
            onClose={() => setSelectedInterestPoint(null)}
          />
        )}

        {/* Legend */}
        <div className="absolute bottom-4 left-4 z-20">
          {/* <div
            className={`${themeClasses.bg} rounded-lg shadow-lg border ${themeClasses.border} p-3`}
          >
            <div className={`text-sm font-semibold ${themeClasses.text} mb-2`}>
              Resolución H3: {getH3Resolution(currentZoom || DEFAULT_ZOOM)} {showGrid ? "(Visible)" : "(Oculta)"}
            </div>
            <div className={`text-xs ${themeClasses.textSecondary} mb-2`}>
              Zoom: {currentZoom}
            </div>
          </div> */}
        </div>

        {/* Ficha de la unidad: ventana dentro del mapa, bajo la lupa. */}
        {selectedUnit && !cardMinimized && (
          <UnitInfoModal
            unit={selectedUnit}
            fromPill={cardFromPill}
            onMinimize={() => setCardMinimized(true)}
            onClose={() => {
              setSelectedUnit(null);
              setCardMinimized(false);
            }}
            isDarkMode={isDarkMode}
          />
        )}

        {/* Buscador de direcciones: lupa junto a pantalla completa. */}
        <AddressSearch map={mapInstance} open={showSearchBox} onOpenChange={setShowSearchBox} />

        {/* Pantalla completa del área del mapa (con píldoras, ficha y vista de
            manejo); la de Google sólo agrandaba el lienzo y dejaba fuera todo eso. */}
        <button
          type="button"
          onClick={(e) => {
            if (document.fullscreenElement) void document.exitFullscreen();
            else void (e.currentTarget.closest("[data-map-area]") ?? e.currentTarget.parentElement)?.requestFullscreen();
          }}
          aria-label={t("map.fullscreen", "Pantalla completa")}
          title={t("map.fullscreen", "Pantalla completa")}
          className={`absolute top-3 right-3 z-20 ${mapIconButton(isDarkMode)}`}
        >
          <i className="ri-fullscreen-line text-[17px]" aria-hidden />
        </button>

        {/* Zoom: en la esquina de abajo a la derecha, donde estaban las flechas de Google. */}
        <div className={`absolute bottom-8 right-3 z-20 flex flex-col rounded-[10px] ${mapChromeBase} ${mapChrome(isDarkMode).surface}`}>
          {([["ri-add-line", 1, "map.zoomIn", "Acercar"], ["ri-subtract-line", -1, "map.zoomOut", "Alejar"]] as const).map(([icon, d, key, label], i) => (
            <button
              key={icon}
              type="button"
              onClick={() => mapInstance?.setZoom((mapInstance.getZoom() ?? DEFAULT_ZOOM) + d)}
              aria-label={t(key, label)}
              title={t(key, label)}
              className={`size-8 grid place-items-center transition-colors duration-150 ${mapChrome(isDarkMode).hover} ${i ? `border-t ${mapChrome(isDarkMode).line} rounded-b-[9px]` : "rounded-t-[9px]"}`}
            >
              <i className={`${icon} text-base`} aria-hidden />
            </button>
          ))}
        </div>

        <UnitDisplayTool />

        {/* Ruler Info Panel - Top Right Corner */}
        {showRuler && rulerPoints.length > 0 && (
          <div className="absolute top-4 right-4 z-40">
            <div
              className={`${themeClasses.bg} rounded-lg shadow-xl p-2 md:p-4 max-w-xs md:max-w-sm`}
            >
              <div className="flex items-center justify-between mb-2 md:mb-3">
                <div className="flex items-center space-x-1 md:space-x-2">
                  <i className="ri-ruler-line text-primary-500 text-sm md:text-lg"></i>
                  <h3
                    className={`text-xs md:text-sm font-semibold ${themeClasses.text}`}
                  >
                    Regla
                  </h3>
                  {isRulerCollapsed && rulerPoints.length >= 2 && (
                    <span
                      className={`text-[10px] md:text-xs ${themeClasses.textSecondary}`}
                    >
                      ({rulerPoints.length} puntos -{" "}
                      {calculateTotalDistance(rulerPoints).toFixed(2)} km)
                    </span>
                  )}
                </div>
                <div className="flex items-center space-x-0.5 md:space-x-1">
                  <button
                    onClick={() => setIsRulerCollapsed(!isRulerCollapsed)}
                    className={`p-0.5 md:p-1 rounded ${themeClasses.buttonHover} ${themeClasses.textSecondary} transition-colors`}
                    title={isRulerCollapsed ? "Expandir" : "Contraer"}
                  >
                    <i
                      className={`ri-${isRulerCollapsed ? "arrow-down-s" : "arrow-up-s"}-line text-xs md:text-sm`}
                    ></i>
                  </button>
                  <button
                    onClick={() => setRulerPoints([])}
                    className={`p-0.5 md:p-1 rounded ${themeClasses.buttonHover} ${themeClasses.textSecondary} hover:text-red-500 transition-colors`}
                    title="Limpiar todos los puntos"
                  >
                    <i className="ri-delete-bin-line text-xs md:text-sm"></i>
                  </button>
                </div>
              </div>

              {!isRulerCollapsed && (
                <>
                  {/* Points List */}
                  <div className="max-h-64 md:max-h-96 overflow-y-auto">
                    <div className="space-y-1 md:space-y-2 mb-2 md:mb-3 border-t">
                      {rulerPoints.map((point, index) => (
                        <div key={index} className={`p-1 md:p-2 border-b`}>
                          <div className="flex items-center gap-1 md:gap-2 justify-between mb-0.5 md:mb-1">
                            <span
                              className={`text-[10px] md:text-xs font-semibold ${themeClasses.text}`}
                            >
                              {index + 1}
                            </span>
                            <i className="ri-map-pin-2-line text-xs md:text-sm"></i>
                            <div
                              className={`text-[10px] md:text-sm ${themeClasses.textSecondary} font-mono`}
                            >
                              <div>
                                {point.lat.toFixed(6)}, {point.lng.toFixed(6)}
                              </div>
                            </div>
                            <button
                              onClick={() => {
                                setRulerPoints((prev) =>
                                  prev.filter((_, i) => i !== index),
                                );
                              }}
                              className={`flex items-center gap-1 md:gap-2 p-0.5 md:p-1 rounded ${themeClasses.buttonHover} ${themeClasses.textSecondary} hover:text-red-500 transition-colors`}
                              title="Eliminar punto"
                            >
                              <i className="ri-close-line text-[10px] md:text-xs"></i>
                            </button>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* Total Distance */}
                  {rulerPoints.length >= 2 && (
                    <div className={``}>
                      <div className="flex items-center justify-between">
                        <span
                          className={`text-xs md:text-sm font-semibold ${themeClasses.text}`}
                        >
                          Distancia:
                        </span>
                        <div className="text-right">
                          <div
                            className={`text-xs md:text-sm font-bold text-primary-500`}
                          >
                            {calculateTotalDistance(rulerPoints).toFixed(2)} km
                          </div>
                          <div
                            className={`text-[10px] md:text-xs ${themeClasses.textSecondary}`}
                          >
                            {(
                              calculateTotalDistance(rulerPoints) * 1000
                            ).toFixed(0)}{" "}
                            m
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </>
              )}
            </div>
          </div>
        )}

        {/* Regla activa: sólo la indicación, chiquita. El globo de
            «Herramientas activas» (borde verde, chips de colores) salía también al
            prender geocercas, rutas o tráfico, cosa que ya dice el punto de cada
            píldora (Daniel, 30-oct). Abajo al centro: arriba a la izquierda va
            «Siguiendo». */}
        {showRuler && (
          <div className="absolute bottom-[4.75rem] left-1/2 -translate-x-1/2 z-30 pointer-events-none">
            <p className={`inline-flex items-center gap-1.5 h-7 px-2.5 rounded-[10px] border text-xs shadow-[0_1px_3px_rgba(0,0,0,0.08)] backdrop-blur-md ${isDarkMode ? "bg-[#1c1c1c]/85 border-white/10 text-gray-300" : "bg-white/85 border-black/[0.08] text-gray-600"}`}>
              <i className="ri-ruler-line text-sm" aria-hidden />
              {rulerPoints.length === 0
                ? "Haz clic en el mapa para agregar puntos"
                : "Sigue haciendo clic para agregar más puntos o arrastra los existentes"}
            </p>
          </div>
        )}

      </div>

      {loadErrorConfig && (
        <div style={{position: 'fixed', inset: 0, backgroundColor: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(4px)', zIndex: 40, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '16px'}}>
          <div style={{backgroundColor: isDarkMode ? '#3A3A3A' : 'white', borderRadius: '16px', border: `1px solid ${isDarkMode ? '#4B5563' : '#e2e8f0'}`, boxShadow: '0 20px 25px rgba(0,0,0,0.1)', width: '100%', maxWidth: '448px', overflow: 'hidden'}}>
            <div style={{background: 'linear-gradient(135deg, #ef4444, #dc2626)', padding: '32px 24px', textAlign: 'center'}}>
              <div style={{display: 'flex', justifyContent: 'center', marginBottom: '16px'}}>
                <div style={{width: '64px', height: '64px', backgroundColor: isDarkMode ? 'rgba(0, 0, 0, 0.25)' : 'rgba(255, 255, 255, 0.85)', borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                  <i className="ri-wifi-off-line" style={{fontSize: '32px', color: '#ef4444'}}></i>
                </div>
              </div>
              <h2 style={{fontSize: '24px', fontWeight: 'bold', color: 'white'}}>{loadErrorConfig.title}</h2>
            </div>

            <div style={{padding: '24px'}}>
              <p style={{textAlign: 'center', color: isDarkMode ? '#d1d5db' : '#334155', fontSize: '15px', marginBottom: '24px'}}>
                {loadErrorConfig.message}
              </p>

              <div style={{display: 'flex', flexDirection: 'column', gap: '12px'}}>
                <button
                  onClick={handleRetryConnection}
                  style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '16px', background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: 'white', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', fontSize: '14px', boxShadow: '0 4px 6px rgba(0,0,0,0.1)'}}
                >
                  <i className="ri-refresh-line"></i>
                  <span>{t("devicesLoadError.retry")}</span>
                </button>

                <button
                  onClick={() => window.location.reload()}
                  style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '16px', backgroundColor: isDarkMode ? '#4b5563' : '#e5e7eb', color: isDarkMode ? 'white' : '#0f172a', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'}}
                >
                  <i className="ri-restart-line"></i>
                  <span>{t("devicesLoadError.reload")}</span>
                </button>

                <button
                  onClick={() => setDismissedLoadError(devicesLoadError)}
                  style={{width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', padding: '12px', backgroundColor: 'transparent', color: isDarkMode ? '#a1a5b4' : '#64748b', border: 'none', borderRadius: '12px', cursor: 'pointer', fontWeight: '600', fontSize: '14px'}}
                >
                  <i className="ri-close-line"></i>
                  <span>{t("devicesLoadError.dismiss")}</span>
                </button>
              </div>

              <p style={{fontSize: '12px', color: isDarkMode ? '#a1a5b4' : '#64748b', textAlign: 'center', marginTop: '16px'}}>
                {t("devicesLoadError.persist")}
              </p>
            </div>
          </div>
        </div>
      )}


    </>
  );
}